import { useState, useRef, useEffect, useCallback } from "react";

const APP_VERSION = "v5.0";

// ─── Custom Select ────────────────────────────────────────────────────────────
function CustomSelect({ options, value, onChange, placeholder = "Select…" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: "100%", background: "#111118",
        border: `1px solid ${open ? "#a07010" : "#2a2a38"}`,
        color: value ? "#e8e8e0" : "#3a3a4a",
        fontFamily: "'DM Mono','Courier New',monospace", fontSize: "13px",
        padding: "10px 36px 10px 14px", textAlign: "left", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        transition: "border-color 0.2s", outline: "none",
      }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value || placeholder}
        </span>
        <span style={{ color: "#f0a500", fontSize: "9px", flexShrink: 0, marginLeft: 8,
          display: "inline-block", transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s" }}>▼</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0,
          background: "#1a1a24", border: "1px solid #2a2a38", zIndex: 300,
          maxHeight: "220px", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}>
          {options.map(opt => (
            <div key={opt} onClick={() => { onChange(opt); setOpen(false); }}
              style={{ padding: "9px 14px", fontSize: "13px",
                color: opt === value ? "#f0a500" : "#c8c8be",
                background: opt === value ? "rgba(240,165,0,0.08)" : "transparent",
                cursor: "pointer", borderBottom: "1px solid rgba(42,42,56,0.5)",
                fontFamily: "'DM Mono','Courier New',monospace" }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = "transparent"; }}>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ESG Framework Picker ─────────────────────────────────────────────────────
const ESG_OPTIONS = [
  ["ISSB / IFRS S1+S2", "TCFD",     "EU CSRD / ESRS"],
  ["GRI Standards",     "CDP",       "SASB"          ],
];

function ESGFrameworkPicker({ value = [], otherValue = "", onChange, onOtherChange }) {
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter(v => v !== opt) : [...selected, opt];
    onChange(next);
  };
  return (
    <div style={{ border: "1px solid var(--border)", background: "var(--bg2)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
        {ESG_OPTIONS.map((row, ri) =>
          row.map((opt, ci) => {
            const checked = selected.includes(opt);
            return (
              <div key={opt} onClick={() => toggle(opt)}
                style={{
                  display: "flex", alignItems: "center", gap: 9,
                  padding: "10px 14px", cursor: "pointer",
                  background: checked ? "rgba(0,200,255,0.05)" : "transparent",
                  borderBottom: ri === 0 ? "1px solid var(--border)" : "none",
                  borderRight: ci < 2 ? "1px solid var(--border)" : "none",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = checked ? "rgba(0,200,255,0.05)" : "transparent"; }}>
                <div style={{ width: 13, height: 13, flexShrink: 0,
                  border: `1px solid ${checked ? "var(--amber)" : "var(--border)"}`,
                  background: checked ? "var(--amber)" : "var(--bg3)",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {checked && <span style={{ fontSize: "9px", color: "var(--bg)", fontWeight: 700, lineHeight: 1 }}>✓</span>}
                </div>
                <span style={{ fontSize: "12px", color: checked ? "var(--amber)" : "var(--text)", lineHeight: 1.3 }}>{opt}</span>
              </div>
            );
          })
        )}
        {/* Other cell — col 1 of row 3 */}
        <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)", gridColumn: "1 / -1",
          display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: "11px", color: "var(--muted)", letterSpacing: "0.1em", flexShrink: 0 }}>Other:</span>
          <input
            type="text"
            value={otherValue}
            onChange={e => onOtherChange(e.target.value)}
            placeholder="e.g. BRSR, TNFD, local regulation…"
            style={{ flex: 1, background: "transparent", border: "none", borderBottom: "1px solid var(--border)",
              color: "var(--text)", fontFamily: "'DM Mono','Courier New',monospace", fontSize: "12px",
              padding: "2px 4px", outline: "none" }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Risk Definitions ─────────────────────────────────────────────────────────
const RISK_CATEGORIES = [
  {
    id: "regulatory",
    label: "Regulatory & Permitting",
    items: [
      { id: "zoning", name: "Zoning / land use", severity: "high",
        context: "Misaligned zoning can halt a project before a shovel hits the ground. Industrial or mixed-use designations are ideal; agricultural or residential require rezoning that adds 6–18 months.",
        mitigation: "Engage land use counsel pre-LOI. Confirm existing entitlements and any overlay restrictions before committing capital." },
      { id: "building_permits", name: "Building permits", severity: "moderate",
        context: "Permit timelines vary widely by jurisdiction — from 4 weeks in business-friendly markets to 12+ months in constrained urban areas. Incomplete submissions are the #1 cause of delays.",
        mitigation: "Pre-application meetings with AHJ (Authority Having Jurisdiction) reduce cycle time. Engage a local expediter in complex markets." },
      { id: "environmental", name: "Environmental review", severity: "high",
        context: "NEPA/CEQA reviews, Phase I/II ESA findings, and wetlands determinations can add significant time and cost. Brownfield sites carry elevated remediation risk.",
        mitigation: "Commission Phase I ESA before LOI. Scope Phase II if Phase I flags recognized environmental conditions. Budget 10–15% contingency for remediation." },
      { id: "fire_safety", name: "Fire / life safety code", severity: "moderate",
        context: "High-density battery storage (BESS) and liquid cooling systems trigger NFPA 855 and local fire codes that vary significantly. Non-compliant designs require costly redesign.",
        mitigation: "Engage fire protection engineer early. Confirm local AHJ interpretation of NFPA 855 before finalizing MEP design." },
      { id: "utility_interconnect", name: "Utility interconnect approval", severity: "critical",
        context: "Frequently the #1 critical path risk. Interconnection queue positions can take 2–5 years in constrained markets. Transmission upgrades triggered by large loads shift costs to the developer.",
        mitigation: "File interconnection application immediately — queue position is everything. Engage utility liaison and transmission attorney. Model worst-case upgrade costs in pro forma." },
    ],
  },
  {
    id: "power",
    label: "Power & Energy",
    items: [
      { id: "grid_capacity", name: "Grid capacity", severity: "critical",
        context: "Available capacity at the substation level does not guarantee deliverable capacity for large AI loads. Grid constraints can force load curtailment agreements or expensive upgrades.",
        mitigation: "Request load flow study from utility. Validate available fault current and transformer capacity. Avoid markets with known congestion without hedging strategy." },
      { id: "generator_lead", name: "Generator / UPS lead times", severity: "critical",
        context: "Large diesel generators (2MW+) currently carry 12–24 month lead times. UPS systems are similarly constrained. This is the single most common schedule killer in data center construction.",
        mitigation: "Place generator POs at project sanction, not permit approval. Consider speculative procurement or pre-positioned inventory for fast-track projects." },
      { id: "redundancy", name: "Redundancy architecture", severity: "high",
        context: "Tier classification (I–IV) drives significantly different capex. AI inference workloads may tolerate Tier II/III redundancy, but hyperscaler tenants typically require Tier III minimum.",
        mitigation: "Align redundancy architecture to target tenant requirements early. Over-building for Tier IV when tenants accept Tier III destroys yield." },
      { id: "utility_rate", name: "Utility rate / PPA structure", severity: "high",
        context: "Power is 40–60% of data center opex. Unfavorable rate structures, demand charges, or inability to execute a PPA can make a site uneconomic regardless of other attributes.",
        mitigation: "Model blended power cost under current tariff and projected PPA scenarios. Engage utility economic development team early — incentive rates are often available for large loads." },
      { id: "fuel_supply", name: "Fuel supply", severity: "moderate",
        context: "Diesel supply continuity for extended generator runs (72h+) requires fuel contracts and on-site storage. Remote sites with limited road access are particularly vulnerable.",
        mitigation: "Execute fuel supply agreement with primary and backup suppliers. Size on-site storage for 72h minimum at full load. Include fuel polishing in maintenance plan." },
    ],
  },
  {
    id: "construction",
    label: "Construction & Procurement",
    items: [
      { id: "long_lead", name: "Long-lead equipment", severity: "critical",
        context: "Transformers (40–80 weeks), switchgear (40–60 weeks), and generators (52–104 weeks) are on extended lead times globally. Missing procurement windows delays COD by quarters.",
        mitigation: "Issue LOIs and POs for all long-lead equipment at financial close. Do not wait for permits. Model procurement schedule alongside construction CPM." },
      { id: "labor", name: "Labor / contractor availability", severity: "high",
        context: "Skilled electrical and mechanical trades are in short supply in most major markets. Subcontractor capacity constraints add 10–20% to labor costs and extend schedules.",
        mitigation: "Pre-qualify and secure GC/subs before permitting. Consider markets with lower labor competition. Lock in workforce with early engagement letters." },
      { id: "cost_escalation", name: "Cost escalation", severity: "high",
        context: "Material costs have escalated 20–40% since 2021 and remain volatile. Fixed-price contracts are difficult to secure; GMP contracts shift risk but at a premium.",
        mitigation: "Include 15–20% construction contingency in pro forma. Use escalation clauses with commodity indices. Consider phased delivery to reduce exposure." },
      { id: "geotech", name: "Geotech surprises", severity: "moderate",
        context: "Poor soil bearing capacity, high groundwater, rock outcroppings, or expansive soils can add $2–10M to foundation costs and delay schedule by 3–6 months.",
        mitigation: "Commission geotechnical investigation (borings and lab analysis) before design. Avoid sites with known karst, fill, or contamination without adequate contingency." },
      { id: "switchgear", name: "Switchgear lead times", severity: "critical",
        context: "Medium-voltage switchgear is at 40–80 week lead times from major manufacturers. Spot market sourcing carries significant risk of counterfeit or non-compliant equipment.",
        mitigation: "Procure switchgear simultaneously with transformers. Validate manufacturer allocation windows. Consider used/refurbished only from certified sources with full warranty." },
    ],
  },
  {
    id: "cooling",
    label: "Cooling & Infrastructure",
    items: [
      { id: "cooling_capacity", name: "Cooling capacity headroom", severity: "high",
        context: "AI GPU clusters run at 40–100kW per rack, far exceeding traditional data center cooling design assumptions. Undersized cooling systems limit deployable density and create hotspots.",
        mitigation: "Design cooling for 1.5x anticipated peak load. Model PUE at target density. Validate chiller and CRAC capacity before committing rack layout." },
      { id: "water_rights", name: "Water rights", severity: "high",
        context: "Evaporative cooling systems can consume millions of gallons annually. Water scarcity, drought restrictions, and water rights constraints can limit or prohibit certain cooling approaches.",
        mitigation: "Confirm water availability and rights with local municipality before selecting cooling strategy. Consider dry coolers or liquid cooling to reduce water dependence in constrained markets." },
      { id: "bms", name: "BMS integration complexity", severity: "moderate",
        context: "Protocol mismatches across vendors (BACnet, Modbus, Lonworks) create commissioning delays and ongoing management challenges. Schneider, Vertiv, and Eaton systems rarely interoperate natively.",
        mitigation: "Define BMS integration requirements in equipment specs. Use a master systems integrator. Validate protocol compatibility before procurement. Budget 10–15% of BMS cost for integration." },
      { id: "liquid_cooling", name: "Liquid cooling readiness", severity: "moderate",
        context: "Direct liquid cooling (DLC) and immersion cooling require specialized infrastructure, maintenance protocols, and supply chain support that many operators lack. Retrofitting air-cooled facilities is expensive.",
        mitigation: "Design liquid cooling infrastructure into the base build for AI-targeted facilities. Engage cooling technology vendors early for specification support and support commitments." },
      { id: "thermal_modeling", name: "Thermal modeling accuracy", severity: "moderate",
        context: "Inaccurate thermal models result in hotspots, equipment failures, and costly retrofits. CFD modeling assumptions that don't match actual rack layout or airflow patterns are a common failure mode.",
        mitigation: "Commission CFD thermal modeling before finalizing floor layout. Validate model against actual conditions during commissioning. Build in airflow management flexibility." },
    ],
  },
  {
    id: "connectivity",
    label: "Connectivity & Network",
    items: [
      { id: "fiber_diversity", name: "Fiber route diversity", severity: "critical",
        context: "Single-conduit fiber ingress is a silent Tier III violation. A single backhoe strike can take down a facility. True path diversity requires physically separate routes into the building.",
        mitigation: "Require dual-path diversity with separate ingress points, separate conduits, and separate carrier infrastructure. Document physical route separation in due diligence." },
      { id: "last_mile", name: "Last-mile construction rights", severity: "high",
        context: "ROW permits, easements, and municipal approvals for last-mile fiber construction can add 6–18 months. Incumbent carriers may not have existing infrastructure on the preferred route.",
        mitigation: "Engage carrier construction teams early. Assess existing ROW availability along planned routes. Budget for conduit installation if carrier doesn't have infrastructure in place." },
      { id: "carrier_ix", name: "Carrier / IX access", severity: "high",
        context: "Limited carrier presence and no proximity to an Internet Exchange increases latency and limits redundancy options. Carrier-neutral facilities command premium rents for good reason.",
        mitigation: "Target sites within 50ms of major IX points. Negotiate carrier-neutral rights in lease. Pre-wire for multi-carrier access even if initially deploying single carrier." },
      { id: "dark_lit", name: "Dark vs. lit fiber strategy", severity: "moderate",
        context: "Dark fiber offers lowest long-term cost and maximum control but requires significant upfront capex for DWDM equipment. Lit services offer faster deployment but lock in carrier pricing.",
        mitigation: "Model dark vs. lit economics over 5–10 year horizon. Negotiate IRU dark fiber rights now even if deploying lit initially. Preserve optionality to light own fiber." },
      { id: "network_debt", name: "Network commissioning debt", severity: "moderate",
        context: "Rushed network commissioning creates technical debt — undocumented configurations, untested failover paths, and deferred testing — that compounds into outage risk over time.",
        mitigation: "Require full network commissioning documentation as a condition of substantial completion. Test all redundant paths under load. Budget for dedicated network commissioning resources." },
    ],
  },
];

const ALL_RISKS = RISK_CATEGORIES.flatMap(c => c.items);

// ─── Risk Item (simple, no tooltip) ──────────────────────────────────────────
function RiskItem({ item, checked, onToggle, sevColor, sevBg, sevBorder }) {
  return (
    <div onClick={onToggle}
      style={{ display: "flex", alignItems: "center", gap: "10px",
        background: checked ? "rgba(240,165,0,0.05)" : "#1a1a24",
        border: `1px solid ${checked ? "#a07010" : "#2a2a38"}`,
        padding: "9px 12px", cursor: "pointer", transition: "all 0.15s" }}>
      <div style={{ width: "14px", height: "14px", border: `1px solid ${checked ? "#f0a500" : "#2a2a38"}`,
        background: checked ? "#00c8ff" : "#080c12", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        {checked && <span style={{ fontSize: "10px", color: "#080c12", fontWeight: 700, lineHeight: 1 }}>✓</span>}
      </div>
      <div style={{ flex: 1, fontSize: "11px", color: checked ? "#e8e8e0" : "#c8c8be" }}>{item.name}</div>
      <span style={{ fontSize: "8px", letterSpacing: "0.1em", padding: "1px 5px", borderRadius: "2px",
        color: sevColor[item.severity], background: sevBg[item.severity],
        border: `1px solid ${sevBorder[item.severity]}`, flexShrink: 0 }}>
        {item.severity.toUpperCase()}
      </span>
    </div>
  );
}

// ─── Risk Filter Modal ────────────────────────────────────────────────────────
function RiskFilterModal({ selectedRisks, onApply, onClose }) {
  const [local, setLocal] = useState(new Set(selectedRisks || []));

  // Sync when parent selectedRisks changes (e.g. modal reopened after external update)
  useEffect(() => {
    setLocal(new Set(selectedRisks));
  }, [(selectedRisks || []).join(",")]);

  const toggle = (id) => {
    setLocal(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const sevColor = { critical: "#ff6666", high: "#c88800", moderate: "#888890" };
  const sevBg = { critical: "rgba(255,68,68,0.12)", high: "rgba(240,165,0,0.1)", moderate: "rgba(100,100,120,0.15)" };
  const sevBorder = { critical: "rgba(255,68,68,0.25)", high: "rgba(240,165,0,0.2)", moderate: "#2a2a38" };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) { onApply([...local]); onClose(); } }} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 500, padding: "20px",
    }}>
      <div style={{ background: "#111118", border: "1px solid #2a2a38", width: "100%", maxWidth: "720px",
        maxHeight: "85vh", display: "flex", flexDirection: "column", fontFamily: "'DM Mono','Courier New',monospace" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #2a2a38", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 500, color: "#e8e8e0", letterSpacing: "0.05em" }}>Risk Factor Filter</div>
            <div style={{ fontSize: "10px", color: "#666678", marginTop: 3, letterSpacing: "0.05em" }}>Selected risks appear on Matrix rows and are included in AI analysis</div>
          </div>
          <button onClick={() => { onApply([...local]); onClose(); }} style={{ background: "transparent", border: "1px solid #2a2a38", color: "#666678", fontFamily: "inherit", fontSize: "11px", padding: "5px 10px", cursor: "pointer" }}>✕ Close</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
          {RISK_CATEGORIES.map(cat => (
            <div key={cat.id} style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px", paddingBottom: "7px", borderBottom: "1px solid #1e1e2a" }}>
                <div style={{ fontSize: "10px", letterSpacing: "0.18em", color: "#f0a500", fontWeight: 500 }}>{cat.label.toUpperCase()}</div>
                <div style={{ fontSize: "9px", color: "#666678" }}>{cat.items.length} items</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                {cat.items.map(item => {
                  const checked = local.has(item.id);
                  return (
                    <RiskItem key={item.id} item={item} checked={checked} onToggle={() => toggle(item.id)}
                      sevColor={sevColor} sevBg={sevBg} sevBorder={sevBorder} />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #2a2a38", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: "11px", color: "#666678" }}>
            <span style={{ color: "#f0a500", fontWeight: 500 }}>{local.size}</span> of 25 risks selected
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => setLocal(new Set())} style={{ background: "transparent", border: "1px solid #2a2a38", color: "#666678", fontFamily: "inherit", fontSize: "10px", letterSpacing: "0.12em", padding: "9px 16px", cursor: "pointer" }}>
              Clear All
            </button>
            <button onClick={() => onApply([...local])} style={{ background: "#00c8ff", color: "#080c12", border: "none", fontFamily: "inherit", fontSize: "10px", fontWeight: 600, letterSpacing: "0.15em", padding: "9px 20px", cursor: "pointer" }}>
              Apply Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Field definitions ────────────────────────────────────────────────────────
const FIELDS = [
  { key: "projectName", label: "Project Name", placeholder: "e.g. Project Ironclad" },
  { key: "location", label: "Location", placeholder: "e.g. Reno, NV" },
  { key: "latitude", label: "Latitude", placeholder: "e.g. 39.5296", validate: "lat" },
  { key: "longitude", label: "Longitude", placeholder: "e.g. -119.8138", validate: "lng" },
  { key: "availablePower", label: "Available Power (MW)", type: "select",
    options: ["<5 MW","5–10 MW","10–25 MW","25–50 MW","50–100 MW","100–200 MW","200+ MW"] },
  { key: "expandablePower", label: "Expandable Power", type: "select",
    options: ["No expansion path","Up to 2x in 12 months","Up to 2x in 24 months","Up to 3x in 18 months","Up to 3x in 36 months","5x+ long-term potential"] },
  { key: "distanceToSubstation", label: "Distance to Substation", type: "select",
    options: ["On-site / <0.1 mi","0.1–0.5 mi","0.5–1 mi","1–3 mi","3–10 mi","10+ mi"] },
  { key: "fiberAvailability", label: "Fiber Availability", type: "select",
    options: ["No fiber access","Single-path, leased capacity","Single-path dark fiber","Dual-path, leased capacity","Dual-path dark fiber","Carrier-neutral, multi-provider","On-net exchange / IX colocation"] },
  { key: "latencyRequirements", label: "Latency Requirements", type: "select",
    options: ["<1ms (ultra-low / financial)","<5ms (edge inference)","<10ms (regional inference)","<20ms (standard enterprise)","<50ms (batch / async workloads)","Latency-insensitive"] },
  { key: "targetCustomers", label: "Target Customers", type: "select",
    options: ["Tier-1 hyperscalers (AWS, Azure, GCP)","AI-native startups / inference companies","Enterprise (Fortune 500)","Government / defense / public sector","Telecom / carrier edge","Mixed / diversified"] },
  { key: "landStatus", label: "Land Status", type: "select",
    options: ["Owned, pad-ready, zoned","Owned, requires permitting","Leased, pad-ready","Leased, requires permitting","Under LOI / option","Greenfield, unzoned"] },
  { key: "timeline", label: "Timeline to First Power", type: "select",
    options: ["<3 months","3–6 months","6–12 months","12–18 months","18–24 months","24+ months"] },
  { key: "capitalConstraints", label: "Capital Constraints", placeholder: "e.g. $50M equity committed, seeking $150M debt" },
  { key: "notes", label: "Additional Notes", placeholder: "Any other relevant context...", type: "textarea" },
  { key: "esgFramework", label: "ESG Reporting Framework", type: "esgpicker" },
  { key: "esgNotes", label: "ESG Profile & Commitments", placeholder: "e.g. 100% renewable by 2027, ISSB-aligned disclosures planned, water-neutral target, Scope 2 zero emissions goal, PUE target...", type: "textarea" },
];

// ─── Prompts ──────────────────────────────────────────────────────────────────
const ANALYSIS_SYSTEM = `You are an expert AI infrastructure strategist, investment analyst, and deal advisor specializing in distributed inference, edge computing, and data center development. You have deep knowledge of ESG disclosure frameworks including ISSB (IFRS S1/S2), GRI, SASB, TCFD, CDP, and EU CSRD/ESRS, and their application to data center and AI infrastructure assets.

Respond in clean HTML only. Use <h2> for section headers, <h3> for subsections, <p> for paragraphs, <ul>/<li> for lists, <strong> for emphasis, and <table> for structured data. No markdown, no code fences, just HTML. Do NOT include <style>, <script>, <link>, or any other non-content tags. Use <div class="score-badge"> for the site score. Use <div class="tier-badge tier-a">, tier-b, or tier-c for tier classification. Output must be investor-grade, institutional, concise, actionable.`;

const EXTRACT_SYSTEM = `You are a data extraction specialist for AI infrastructure site documents. Extract structured data from the provided document and return ONLY a valid JSON object — no markdown, no explanation, no code fences.

Map extracted data to these exact keys:
{
  "projectName": string or null,
  "location": string or null,
  "availablePower": one of ["<5 MW","5–10 MW","10–25 MW","25–50 MW","50–100 MW","100–200 MW","200+ MW"] or null,
  "expandablePower": one of ["No expansion path","Up to 2x in 12 months","Up to 2x in 24 months","Up to 3x in 18 months","Up to 3x in 36 months","5x+ long-term potential"] or null,
  "distanceToSubstation": one of ["On-site / <0.1 mi","0.1–0.5 mi","0.5–1 mi","1–3 mi","3–10 mi","10+ mi"] or null,
  "fiberAvailability": one of ["No fiber access","Single-path, leased capacity","Single-path dark fiber","Dual-path, leased capacity","Dual-path dark fiber","Carrier-neutral, multi-provider","On-net exchange / IX colocation"] or null,
  "latencyRequirements": one of ["<1ms (ultra-low / financial)","<5ms (edge inference)","<10ms (regional inference)","<20ms (standard enterprise)","<50ms (batch / async workloads)","Latency-insensitive"] or null,
  "targetCustomers": one of ["Tier-1 hyperscalers (AWS, Azure, GCP)","AI-native startups / inference companies","Enterprise (Fortune 500)","Government / defense / public sector","Telecom / carrier edge","Mixed / diversified"] or null,
  "landStatus": one of ["Owned, pad-ready, zoned","Owned, requires permitting","Leased, pad-ready","Leased, requires permitting","Under LOI / option","Greenfield, unzoned"] or null,
  "timeline": one of ["<3 months","3–6 months","6–12 months","12–18 months","18–24 months","24+ months"] or null,
  "capitalConstraints": string or null,
  "notes": string or null
}

For enum fields, pick the closest matching option from the list. If no match, return null. Return ONLY the JSON object.`;

function buildAnalysisPrompt(fields, selectedRiskIds) {
  const riskSection = selectedRiskIds.length > 0
    ? `\n\nFlagged Risk Factors (address each explicitly in your analysis):\n${
        selectedRiskIds.map(id => {
          const risk = ALL_RISKS.find(r => r.id === id);
          return risk ? `- ${risk.name} [${risk.severity.toUpperCase()}]` : "";
        }).filter(Boolean).join("\n")
      }`
    : "";

  const esgFrameworks = Array.isArray(fields.esgFramework) ? fields.esgFramework : (fields.esgFramework ? [fields.esgFramework] : []);
  const allFrameworks = fields.esgOther ? [...esgFrameworks, `Other: ${fields.esgOther}`] : esgFrameworks;
  const esgSection = (allFrameworks.length > 0) || fields.esgNotes
    ? `\nESG Frameworks: ${allFrameworks.length > 0 ? allFrameworks.join(", ") : "[Not provided]"}\nESG Profile: ${fields.esgNotes || "[Not provided]"}`
    : "";

  const nextSection = selectedRiskIds.length > 0 ? 9 : 8;

  return `Evaluate and structure this AI infrastructure opportunity:

Project Name: ${fields.projectName || "[Not provided]"}
Location: ${fields.location || "[Not provided]"}
Coordinates: ${(fields.latitude && fields.longitude) ? `${fields.latitude}, ${fields.longitude}` : "[Not provided]"}
Available Power (MW): ${fields.availablePower || "[Not provided]"}
Expandable Power: ${fields.expandablePower || "[Not provided]"}
Distance to Substation: ${fields.distanceToSubstation || "[Not provided]"}
Fiber Availability: ${fields.fiberAvailability || "[Not provided]"}
Latency Requirements: ${fields.latencyRequirements || "[Not provided]"}
Target Customers: ${fields.targetCustomers || "[Not provided]"}
Land Status: ${fields.landStatus || "[Not provided]"}
Timeline: ${fields.timeline || "[Not provided]"}
Capital Constraints: ${fields.capitalConstraints || "[Not provided]"}
Notes: ${fields.notes || "[Not provided]"}${esgSection}${riskSection}

1. Executive Summary (investor-grade)
2. Site Feasibility Score (0-100): Power & Grid Readiness (30%), Fiber Connectivity (20%), Land & Permitting (15%), Latency & Market Proximity (15%), Scalability (10%), Risk Factors (10%). Tier A (80-100), B (65-79), C (<65).
3. Recommended Deployment Model: A (Ultra-edge/tower), B (Substation micro), C (Regional hub) — with rationale.
4. Technical Assessment: power viability, latency implications, fiber/network.
5. Financial Model: revenue per MW/year, utilization, scaling potential.
6. Executive Investment Memo (Pre-CIM): market opportunity, architecture, revenue model, capital structure, phasing A→B→C, risks, exit scenarios.
7. Deal Strategy: leverage points, partnership positioning, negotiation approach.
8. ESG Assessment: evaluate this site's environmental footprint (energy source, PUE target, water/cooling approach, Scope 2 emissions path), social considerations (labor market, community impact), and governance posture. Map declared frameworks (${allFrameworks.length > 0 ? allFrameworks.join(", ") : "not specified"}) to specific disclosure requirements — ISSB IFRS S1/S2 baseline, TCFD climate risk, SASB data center sector metrics, GRI operational impacts, EU CSRD double materiality if applicable. Identify gaps and investor disclosure readiness. If no ESG framework declared, recommend appropriate starting point for this asset class.${selectedRiskIds.length > 0 ? `\n${nextSection}. Flagged Risk Analysis: for each flagged risk factor above, provide a specific assessment and mitigation approach for this site.` : ""}

All outputs must be tied to the input data. No generic explanations.`;
}

// ─── Sheets API helpers ───────────────────────────────────────────────────────
const API = "/api/sheets";

async function sheetsGet() {
  const r = await fetch(API);
  if (!r.ok) throw new Error(await r.text());
  return (await r.json()).sites || [];
}
async function sheetsPost(site) {
  const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ site }) });
  if (!r.ok) throw new Error(await r.text());
}
async function sheetsPut(site) {
  const r = await fetch(API, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ site }) });
  if (!r.ok) throw new Error(await r.text());
}
async function sheetsDelete(id) {
  const r = await fetch(API, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) });
  if (!r.ok) throw new Error(await r.text());
}

// ─── Shared helpers ───────────────────────────────────────────────────────────
/** Strip <style>, <script>, and <link> tags from AI-generated HTML so they
 *  cannot inject global CSS/JS rules that break the surrounding app UI. */
function sanitizeAnalysisHtml(raw) {
  return raw
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<link[^>]*>/gi, "");
}

function extractScoreAndTier(html) {
  const scoreMatch = html.match(/class="score-badge"[^>]*>\s*(\d+)/);
  const tierMatch = html.match(/class="tier-badge\s+(tier-[abc])"/i);
  return {
    score: scoreMatch ? parseInt(scoreMatch[1]) : null,
    tier: tierMatch ? tierMatch[1].replace("tier-", "").toUpperCase() : null,
  };
}

const TIER_DESCRIPTIONS = {
  A: "Tier A (80–100) — Site is deployment-ready. Strong power, fiber, land, and market proximity. Suitable for immediate capital commitment and anchor tenant pursuit.",
  B: "Tier B (65–79) — Site is viable with identified gaps. One or more key attributes require remediation before full deployment. Proceed with conditional due diligence.",
  C: "Tier C (<65) — Site has material deficiencies across multiple dimensions. Requires significant work or de-risking before it can support a competitive AI infrastructure offering.",
};

function TierBadge({ tier, score }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const colors = { A: "#00ff88", B: "#f0a500", C: "#ff4444" };
  const t = tier ? String(tier).toUpperCase() : null;
  const c = colors[t] || "#666";

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.left });
    }
    setHovered(true);
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {score != null && <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontWeight: 800, fontSize: 15, color: c }}>{score}</span>}
      {t && (
        <span ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={() => setHovered(false)}
          style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", position: "relative",
            background: `${c}18`, color: c, border: `1px solid ${c}`, padding: "2px 8px", cursor: "default" }}>
          TIER {t}
          {hovered && TIER_DESCRIPTIONS[t] && (
            <div style={{
              position: "fixed",
              top: pos.top,
              left: Math.min(pos.left, window.innerWidth - 320),
              width: "300px",
              background: "#0d0d14",
              border: `1px solid ${c}44`,
              padding: "10px 14px",
              zIndex: 600,
              pointerEvents: "none",
              boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
              fontFamily: "'DM Mono','Courier New',monospace",
              fontWeight: 400,
              letterSpacing: "0.02em",
              fontSize: "10px",
              color: "#c8c8be",
              lineHeight: 1.65,
            }}>
              {TIER_DESCRIPTIONS[t]}
            </div>
          )}
        </span>
      )}
    </span>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ─── Coordinate validation ────────────────────────────────────────────────────
function validateCoord(value, type) {
  if (!value || value.trim() === "") return null;
  const n = parseFloat(value);
  if (isNaN(n)) return "Must be a number";
  if (type === "lat" && (n < -90 || n > 90)) return "Must be between -90 and 90";
  if (type === "lng" && (n < -180 || n > 180)) return "Must be between -180 and 180";
  return null;
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@300;400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  :root{--amber:#00c8ff;--amber-dim:#0076ff;--green:#34d399;--red:#ff4444;
    --bg:#080c12;--bg2:#0e1520;--bg3:#141d2b;--border:#1e2d42;--text:#d4dfe8;--muted:#6a85a0}
  body{background:var(--bg)}
  .header{border-bottom:1px solid var(--border);padding:22px 40px;display:flex;align-items:center;
    justify-content:space-between;position:sticky;top:0;background:rgba(10,10,15,0.96);
    backdrop-filter:blur(12px);z-index:100}
  .logo{display:flex;align-items:center;gap:12px}
  .logo-icon{width:28px;height:28px;background:#00c8ff;
    clip-path:polygon(25% 0%,75% 0%,100% 50%,75% 100%,25% 100%,0% 50%);flex-shrink:0;
    box-shadow:0 0 8px rgba(0,207,255,0.6)}
  .logo-text{font-family:'Bebas Neue',sans-serif;font-size:14px;font-weight:700;
    letter-spacing:0.15em;text-transform:uppercase;color:var(--text)}
  .logo-sub{font-size:10px;color:var(--muted);letter-spacing:0.2em;text-transform:uppercase}
  .status-bar{display:flex;align-items:center;gap:8px;font-size:11px;color:var(--muted);letter-spacing:0.1em}
  .status-dot{width:6px;height:6px;border-radius:50%;background:var(--green);animation:pulse 2s infinite}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
  .tabs{display:flex;border-bottom:1px solid var(--border);background:var(--bg2);padding:0 40px}
  .tab{font-family:'DM Mono','Courier New',monospace;font-size:11px;font-weight:600;
    letter-spacing:0.18em;text-transform:uppercase;padding:14px 20px;cursor:pointer;
    color:var(--muted);border-bottom:2px solid transparent;transition:all 0.2s;
    background:none;border-top:none;border-left:none;border-right:none}
  .tab.active{color:var(--amber);border-bottom-color:var(--amber)}
  .tab-count{display:inline-block;background:var(--bg3);color:var(--muted);font-size:10px;
    padding:1px 6px;margin-left:6px;border:1px solid var(--border)}
  .tab.active .tab-count{background:rgba(240,165,0,0.1);color:var(--amber);border-color:var(--amber-dim)}
  .main{max-width:960px;margin:0 auto;padding:40px 32px}
  .section-tag{display:inline-block;font-size:10px;letter-spacing:0.25em;text-transform:uppercase;
    color:var(--amber);border:1px solid var(--amber-dim);padding:4px 10px;margin-bottom:20px}
  .hero h1{font-family:'Bebas Neue',sans-serif;font-size:clamp(26px,4vw,42px);font-weight:800;
    line-height:1.1;color:var(--text);margin-bottom:10px}
  .hero h1 span{color:var(--amber)}
  .hero p{font-size:13px;color:var(--muted);line-height:1.7;max-width:520px;margin-bottom:32px}
  .edit-banner{background:rgba(240,165,0,0.07);border:1px solid var(--amber-dim);
    padding:12px 18px;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;gap:12px}
  .edit-banner-text{font-size:12px;color:var(--amber);letter-spacing:0.05em}
  .progress-bar-wrap{background:var(--bg3);border:1px solid var(--border);padding:14px 20px;
    margin-bottom:24px;display:flex;align-items:center;gap:16px}
  .progress-track{flex:1;height:3px;background:var(--bg2);overflow:hidden}
  .progress-fill{height:100%;background:var(--amber);transition:width 0.3s ease}
  .progress-label{font-size:11px;color:var(--muted);white-space:nowrap;letter-spacing:0.1em}
  .progress-pct{font-size:11px;color:var(--amber);font-weight:600;white-space:nowrap;min-width:32px;text-align:right}
  .ingest-zone{border:2px dashed var(--border);background:var(--bg2);padding:20px 24px;
    margin-bottom:24px;display:flex;align-items:center;gap:16px;flex-wrap:wrap;
    transition:border-color 0.2s,background 0.2s;cursor:pointer}
  .ingest-zone:hover{border-color:var(--amber-dim)}
  .ingest-zone.drag-over{border-color:var(--amber);background:rgba(240,165,0,0.04)}
  .ingest-icon{font-size:20px;flex-shrink:0}
  .ingest-info{flex:1;min-width:180px}
  .ingest-title{font-size:11px;letter-spacing:0.15em;text-transform:uppercase;color:var(--text);margin-bottom:4px}
  .ingest-sub{font-size:11px;color:var(--muted)}
  .ingest-status{font-size:11px;color:var(--green);margin-top:4px}
  .ingest-err{font-size:11px;color:var(--red);margin-top:4px}
  .btn-upload{background:transparent;border:1px solid var(--border);color:var(--muted);
    font-family:inherit;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;
    padding:10px 18px;cursor:pointer;transition:all 0.2s;white-space:nowrap}
  .btn-upload:hover{border-color:var(--amber-dim);color:var(--amber)}
  .risk-filter-btn{background:transparent;border:1px solid var(--border);color:var(--muted);
    font-family:inherit;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;
    padding:10px 16px;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:7px;white-space:nowrap}
  .risk-filter-btn:hover{border-color:var(--amber-dim);color:var(--amber)}
  .risk-filter-btn.active{border-color:var(--amber);color:var(--amber);background:rgba(240,165,0,0.06)}
  .risk-count-badge{background:var(--amber);color:#0a0a0f;font-size:9px;font-weight:600;padding:1px 6px;border-radius:2px}
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:28px}
  .field-wrap{display:flex;flex-direction:column;gap:5px}
  .field-wrap.full{grid-column:1/-1}
  .field-label{font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:var(--muted);
    display:flex;align-items:center;gap:6px}
  .field-label .dot{width:4px;height:4px;background:var(--amber);border-radius:50%;flex-shrink:0}
  .field-label .extracted{font-size:9px;color:var(--green);letter-spacing:0.1em;margin-left:4px}
  .field-input,.field-textarea{background:var(--bg2);border:1px solid var(--border);
    color:var(--text);font-family:inherit;font-size:14px;padding:10px 14px;
    outline:none;width:100%;transition:border-color 0.2s;resize:none}
  .field-input:focus,.field-textarea:focus{border-color:var(--amber-dim)}
  .field-input::placeholder,.field-textarea::placeholder{color:#3a3a4a}
  .field-textarea{min-height:76px}
  .field-input.extracted-val,.field-textarea.extracted-val{border-color:rgba(0,255,136,0.25)}
  .actions{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
  .btn-primary{background:var(--amber);color:#0a0a0f;border:none;font-family:inherit;
    font-size:12px;font-weight:600;letter-spacing:0.18em;text-transform:uppercase;
    padding:13px 28px;cursor:pointer;transition:opacity 0.2s,transform 0.1s;
    display:flex;align-items:center;gap:10px}
  .btn-primary:hover:not(:disabled){opacity:0.88}
  .btn-primary:active:not(:disabled){transform:scale(0.98)}
  .btn-primary:disabled{opacity:0.4;cursor:not-allowed}
  .btn-secondary{background:transparent;border:1px solid var(--amber-dim);color:var(--amber);
    font-family:inherit;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;
    padding:13px 20px;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:8px}
  .btn-secondary:hover{background:rgba(240,165,0,0.06)}
  .btn-secondary:disabled{opacity:0.4;cursor:not-allowed}
  .btn-ghost{background:transparent;color:var(--muted);border:1px solid var(--border);
    font-family:inherit;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;
    padding:13px 18px;cursor:pointer;transition:all 0.2s}
  .btn-ghost:hover{color:var(--text);border-color:var(--muted)}
  .btn-sm{background:transparent;border:1px solid var(--border);color:var(--muted);
    font-family:inherit;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;
    padding:6px 12px;cursor:pointer;transition:all 0.2s}
  .btn-sm:hover{border-color:var(--amber-dim);color:var(--amber)}
  .btn-sm.danger:hover{border-color:var(--red);color:var(--red)}
  .btn-sm:disabled{opacity:0.4;cursor:not-allowed}
  .spinner{width:13px;height:13px;border:2px solid rgba(0,0,0,0.2);
    border-top-color:#0a0a0f;border-radius:50%;animation:spin 0.7s linear infinite;flex-shrink:0}
  .spinner-amber{width:13px;height:13px;border:2px solid rgba(240,165,0,0.2);
    border-top-color:var(--amber);border-radius:50%;animation:spin 0.7s linear infinite;flex-shrink:0}
  @keyframes spin{to{transform:rotate(360deg)}}
  .save-status-error{font-size:11px;color:var(--red);letter-spacing:0.1em}

  .prog-bar-outer{background:var(--bg3);border:1px solid var(--border);height:4px;width:100%;overflow:hidden;margin-top:14px}
  .prog-bar-inner{height:100%;background:var(--amber);transition:width 0.4s ease}
  .prog-bar-inner.green{background:var(--green)}
  .ingest-prog-wrap{background:var(--bg3);border:1px solid var(--border);padding:14px 18px;margin-bottom:14px}
  .ingest-prog-label{font-size:10px;letter-spacing:0.12em;color:var(--muted);margin-bottom:8px;display:flex;justify-content:space-between}
  .ingest-prog-label span{color:var(--amber)}
  .analysis-prog-wrap{margin-top:20px;padding:0 20px}
  .analysis-prog-label{font-size:10px;letter-spacing:0.12em;color:var(--muted);margin-bottom:8px;display:flex;justify-content:space-between}
  .analysis-prog-label span{color:var(--amber)}
  .loading-state{margin-top:40px;border:1px solid var(--border);background:var(--bg2);padding:36px;text-align:center}
  .loading-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:20px}
  .loading-item{border:1px solid var(--border);padding:10px;font-size:10px;
    letter-spacing:0.15em;text-transform:uppercase;color:var(--muted);animation:flicker 1.5s infinite}
  .loading-item:nth-child(2){animation-delay:0.3s}.loading-item:nth-child(3){animation-delay:0.6s}
  .loading-item:nth-child(4){animation-delay:0.9s}.loading-item:nth-child(5){animation-delay:1.2s}
  .loading-item:nth-child(6){animation-delay:1.5s}
  @keyframes flicker{0%,100%{border-color:var(--border);color:var(--muted)}50%{border-color:var(--amber-dim);color:var(--amber)}}
  .error-state{margin-top:24px;border:1px solid var(--red);background:rgba(255,68,68,0.05);
    padding:14px 18px;font-size:12px;color:var(--red);display:flex;align-items:center;gap:10px}
  .results{margin-top:48px;border-top:1px solid var(--border);padding-top:40px}
  .results-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;flex-wrap:wrap;gap:12px}
  .results-title{font-family:'Bebas Neue',sans-serif;font-size:13px;font-weight:700;
    letter-spacing:0.2em;text-transform:uppercase;color:var(--muted)}
  .timestamp{font-size:10px;color:var(--muted);letter-spacing:0.1em}
  .results-content{background:var(--bg2);border:1px solid var(--border);padding:36px;line-height:1.75}
  .results-content h2{font-family:'Bebas Neue',sans-serif;font-size:17px;font-weight:700;color:var(--amber);
    letter-spacing:0.05em;text-transform:uppercase;margin:32px 0 12px;padding-bottom:7px;
    border-bottom:1px solid var(--border)}
  .results-content h2:first-child{margin-top:0}
  .results-content h3{font-family:'Bebas Neue',sans-serif;font-size:12px;font-weight:600;
    color:var(--text);letter-spacing:0.1em;text-transform:uppercase;margin:18px 0 7px}
  .results-content p{font-size:13px;color:#c8c8be;margin-bottom:10px}
  .results-content ul{margin:7px 0 14px 0;padding:0;list-style:none}
  .results-content li{font-size:13px;color:#c8c8be;padding:5px 0 5px 16px;
    position:relative;border-bottom:1px solid rgba(42,42,56,0.5)}
  .results-content li::before{content:'›';position:absolute;left:0;color:var(--amber)}
  .results-content strong{color:var(--text);font-weight:600}
  .results-content table{width:100%;border-collapse:collapse;font-size:12px;margin:14px 0}
  .results-content th{background:var(--bg3);color:var(--amber);text-align:left;
    padding:9px 13px;font-size:10px;letter-spacing:0.15em;text-transform:uppercase;border:1px solid var(--border)}
  .results-content td{padding:8px 13px;color:#c8c8be;border:1px solid var(--border);vertical-align:top}
  .results-content tr:nth-child(even) td{background:rgba(26,26,36,0.5)}
  .results-content .score-badge{display:inline-block;background:var(--bg3);border:2px solid var(--amber);
    color:var(--amber);font-family:'Bebas Neue',sans-serif;font-size:34px;font-weight:800;
    padding:10px 22px;margin:7px 0;letter-spacing:0.05em}
  .results-content .tier-badge{display:inline-block;font-family:'Bebas Neue',sans-serif;font-size:11px;
    font-weight:700;letter-spacing:0.2em;text-transform:uppercase;padding:4px 13px;
    margin-left:10px;vertical-align:middle}
  .results-content .tier-a{background:rgba(0,255,136,0.1);color:#00ff88;border:1px solid #00ff88}
  .results-content .tier-b{background:rgba(240,165,0,0.1);color:var(--amber);border:1px solid var(--amber)}
  .results-content .tier-c{background:rgba(255,68,68,0.1);color:var(--red);border:1px solid var(--red)}
  .filter-bar{display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-bottom:16px}
  .filter-badge{display:inline-flex;align-items:center;gap:5px;padding:4px 11px;font-size:10px;
    font-family:'DM Mono','Courier New',monospace;letter-spacing:0.1em;text-transform:uppercase;
    border:1px solid #2e4060;background:transparent;color:#6a85a0;cursor:pointer;
    transition:border-color 0.15s,color 0.15s,background 0.15s;white-space:nowrap}
  .filter-badge:hover{border-color:var(--amber);color:var(--text)}
  .filter-badge .badge-count{font-size:14px;opacity:0.65;margin-left:2px}
  .filter-badge.active-a{border-color:#00ff88;color:#00ff88;background:rgba(0,255,136,0.08)}
  .filter-badge.active-b{border-color:var(--amber);color:var(--amber);background:rgba(0,200,255,0.08)}
  .filter-badge.active-c{border-color:var(--red);color:var(--red);background:rgba(255,68,68,0.08)}
  .filter-badge.active-stale{border-color:#f0a500;color:#f0a500;background:rgba(240,165,0,0.08)}
  .filter-badge.active-risks{border-color:#ff8c42;color:#ff8c42;background:rgba(255,140,66,0.08)}
  .filter-badge.clear-badge{border-color:#2e4060;color:#6a85a0}
  .filter-badge.clear-badge:hover{border-color:var(--red);color:var(--red)}
  .filter-divider{width:1px;height:14px;background:#2e4060;flex-shrink:0;margin:0 2px}
  .repo-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:24px}
  .repo-title{font-family:'Bebas Neue',sans-serif;font-size:32px;font-weight:800;color:var(--text)}
  .repo-empty{border:1px dashed var(--border);padding:60px 40px;text-align:center}
  .repo-empty-title{font-family:'Bebas Neue',sans-serif;font-size:24px;font-weight:700;
    color:var(--muted);letter-spacing:0.1em;margin-bottom:8px}
  .repo-empty-sub{font-size:21px;color:var(--muted)}
  .site-list{display:flex;flex-direction:column;gap:8px}
  .site-card{border:1px solid var(--border);background:var(--bg2);padding:16px 20px;
    display:flex;align-items:center;gap:14px;cursor:pointer;transition:border-color 0.2s}
  .site-card:hover{border-color:var(--amber-dim)}
  .site-card.active{border-color:var(--amber)}
  .site-card-info{flex:1;min-width:0}
  .site-card-name{font-family:'Bebas Neue',sans-serif;font-size:22px;font-weight:700;
    color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .site-card-meta{font-size:18px;color:var(--muted);margin-top:3px;display:flex;gap:10px;flex-wrap:wrap}
  .site-card-actions{display:flex;gap:6px;flex-shrink:0}
  .site-detail{margin-top:24px}
  .site-detail-header{display:flex;align-items:flex-start;justify-content:space-between;
    margin-bottom:20px;gap:16px;flex-wrap:wrap}
  .site-detail-name{font-family:'Bebas Neue',sans-serif;font-size:29px;font-weight:800;color:var(--text)}
  .site-detail-meta{font-size:18px;color:var(--muted);margin-top:4px}
  .sq-tier{display:flex;flex-direction:column;align-items:center;justify-content:center;
    border:1px solid;flex-shrink:0}
  .sq-tier-sm{width:46px;height:46px}
  .sq-tier-lg{width:56px;height:56px}
  .sq-tier-score{font-family:'Bebas Neue',sans-serif;font-weight:800;line-height:1;font-size:20px}
  .sq-tier-lg .sq-tier-score{font-size:24px}
  .sq-tier-label{font-size:8px;letter-spacing:0.14em;opacity:0.8;margin-top:1px}
  .sq-tier-a{border-color:#00ff88;color:#00ff88;background:rgba(0,255,136,0.06)}
  .sq-tier-b{border-color:#f0a500;color:#f0a500;background:rgba(240,165,0,0.06)}
  .sq-tier-c{border-color:#ff4444;color:#ff4444;background:rgba(255,68,68,0.06)}
  .sq-tier-none{border-color:#2e4060;color:#6a85a0;background:transparent}
  .site-fields-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:20px}
  .site-field{background:var(--bg3);border:1px solid var(--border);padding:10px 14px}
  .site-field-label{font-size:14px;letter-spacing:0.18em;text-transform:uppercase;color:var(--muted);margin-bottom:4px}
  .site-field-value{font-size:21px;color:var(--text)}
  .divider{border:none;border-top:1px solid var(--border);margin:24px 0}
  .sheets-badge{display:flex;align-items:center;gap:6px;font-size:16px;color:var(--muted);letter-spacing:0.1em}
  .sheets-dot{width:5px;height:5px;border-radius:50%;background:var(--green)}
  .risk-flag{display:inline-block;font-size:13px;padding:1px 5px;border-radius:2px;letter-spacing:0.05em;margin:1px}
  .risk-flag.critical{background:rgba(255,68,68,0.12);color:#ff6666;border:1px solid rgba(255,68,68,0.25)}
  .risk-flag.high{background:rgba(240,165,0,0.1);color:#c88800;border:1px solid rgba(240,165,0,0.2)}
  .risk-flag.moderate{background:rgba(100,100,120,0.15);color:#888890;border:1px solid #2a2a38}
  .mx-main{max-width:100%;margin:0 auto;padding:40px 32px}
  .mx-toolbar{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;gap:12px;flex-wrap:wrap}
  .mx-toolbar-left{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .mx-stats{display:flex;gap:8px}
  .mx-stat{background:var(--bg3);border:1px solid var(--border);padding:8px 14px;text-align:center;border-radius:2px}
  .mx-stat-val{font-size:26px;font-weight:500}
  .mx-stat-label{font-size:14px;color:var(--muted);letter-spacing:0.1em;margin-top:2px}
  .mx-sort-row{display:flex;align-items:center;gap:8px;margin-bottom:12px;flex-wrap:wrap}
  .mx-sort-label{font-size:14px;color:var(--muted);letter-spacing:0.1em;white-space:nowrap}
  .mx-sort-btn{font-size:14px;letter-spacing:0.1em;color:var(--muted);border:1px solid var(--border);
    background:transparent;padding:4px 10px;border-radius:2px;cursor:pointer;font-family:inherit;transition:all 0.2s}
  .mx-sort-btn.active{color:var(--amber);border-color:var(--amber-dim);background:rgba(240,165,0,0.06)}
  .mx-table-wrap{overflow-x:auto;margin-bottom:16px}
  .mx-table{width:100%;border-collapse:collapse;table-layout:auto}
  .mx-table th{background:var(--bg3);border:1px solid var(--border);padding:9px 12px;font-size:14px;
    letter-spacing:0.15em;color:var(--muted);text-align:left;font-weight:500;white-space:nowrap;
    cursor:pointer;font-family:inherit;transition:color 0.2s}
  .mx-table th:hover{color:var(--text)}
  .mx-table th.sorted{color:var(--amber)}
  .mx-table td{border:1px solid #1e1e2a;padding:10px 12px;font-size:21px;color:var(--muted);vertical-align:middle;word-wrap:break-word;overflow-wrap:break-word}
  .mx-table tr:hover td{background:rgba(255,255,255,0.02)}
  .mx-site-name{font-weight:500;color:var(--text);font-size:21px}
  .mx-site-loc{color:var(--muted);font-size:18px;margin-top:2px}
  .mx-score-wrap{display:flex;align-items:center;gap:6px}
  .mx-score-bar-track{flex:1;height:5px;background:var(--bg3);border-radius:1px;overflow:hidden}
  .mx-score-bar-fill{height:100%;border-radius:1px}
  .mx-score-num{font-size:21px;font-weight:600;min-width:29px;color:var(--text)}
  .mx-pill{display:inline-block;font-size:14px;padding:2px 7px;border-radius:2px;
    border:1px solid var(--border);color:var(--muted);background:var(--bg3)}
  .mx-pill.good{border-color:rgba(0,255,136,0.2);color:#00cc66;background:rgba(0,255,136,0.05)}
  .mx-pill.warn{border-color:rgba(240,165,0,0.2);color:#c88800;background:rgba(240,165,0,0.05)}
  .mx-pill.bad{border-color:rgba(255,68,68,0.2);color:#cc3333;background:rgba(255,68,68,0.05)}
  .mx-footer{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px}
  .mx-footer-note{font-size:16px;color:var(--muted);display:flex;align-items:center;gap:6px}
  .mx-legend{font-size:16px;color:var(--muted);letter-spacing:0.06em}
  .mx-export-btn{background:transparent;border:1px solid var(--border);color:var(--muted);
    font-family:inherit;font-size:16px;letter-spacing:0.12em;text-transform:uppercase;
    padding:7px 14px;cursor:pointer;transition:all 0.2s}
  .mx-export-btn:hover{border-color:var(--amber-dim);color:var(--amber)}
  .mx-empty{border:1px dashed var(--border);padding:60px 40px;text-align:center}
  .mx-empty-title{font-family:'Bebas Neue',sans-serif;font-size:24px;font-weight:700;
    color:var(--muted);letter-spacing:0.1em;margin-bottom:8px}
  .mx-empty-sub{font-size:21px;color:var(--muted)}

  .field-input.coord-error{border-color:rgba(255,68,68,0.5)}
  .field-input.coord-valid{border-color:rgba(0,255,136,0.25)}
  .coord-error-msg{font-size:9px;color:var(--red);margin-top:3px;letter-spacing:0.05em}
  @media(max-width:640px){
    .header{padding:18px}.tabs{padding:0 20px}.main{padding:28px 18px}
    .form-grid{grid-template-columns:1fr}.field-wrap.full{grid-column:1}
    .results-content{padding:20px}.loading-grid{grid-template-columns:1fr 1fr}
    .site-fields-grid{grid-template-columns:1fr 1fr}
  }
`;

// ─── Matrix helpers ───────────────────────────────────────────────────────────
const MATRIX_COLS = [
  { key: "score",                label: "Score",       sortable: true  },
  { key: "availablePower",       label: "Power",       sortable: false },
  { key: "expandablePower",      label: "Expansion",   sortable: false },
  { key: "distanceToSubstation", label: "Substation",  sortable: false },
  { key: "fiberAvailability",    label: "Fiber",       sortable: false },
  { key: "landStatus",           label: "Land",        sortable: false },
  { key: "timeline",             label: "Timeline",    sortable: false },
  { key: "targetCustomers",      label: "Customers",   sortable: false },
  { key: "esgFramework",         label: "ESG",         sortable: false },
];

const POWER_ORDER = ["<5 MW","5–10 MW","10–25 MW","25–50 MW","50–100 MW","100–200 MW","200+ MW"];
const EXPAND_ORDER = ["No expansion path","Up to 2x in 12 months","Up to 2x in 24 months","Up to 3x in 18 months","Up to 3x in 36 months","5x+ long-term potential"];
const FIBER_ORDER = ["No fiber access","Single-path, leased capacity","Single-path dark fiber","Dual-path, leased capacity","Dual-path dark fiber","Carrier-neutral, multi-provider","On-net exchange / IX colocation"];
const SUBSTATION_ORDER = ["On-site / <0.1 mi","0.1–0.5 mi","0.5–1 mi","1–3 mi","3–10 mi","10+ mi"];
const LAND_ORDER = ["Owned, pad-ready, zoned","Leased, pad-ready","Owned, requires permitting","Leased, requires permitting","Under LOI / option","Greenfield, unzoned"];
const TIMELINE_ORDER = ["<3 months","3–6 months","6–12 months","12–18 months","18–24 months","24+ months"];

function pillClass(key, value) {
  if (!value) return "";
  const goodThreshold = (arr, val, good, warn) => {
    const i = arr.indexOf(val);
    if (i === -1) return "";
    if (i < good) return "good";
    if (i < warn) return "warn";
    return "bad";
  };
  if (key === "availablePower")       return goodThreshold(POWER_ORDER, value, 3, 5);
  if (key === "expandablePower")      return value === "No expansion path" ? "bad" : value.includes("3x") || value.includes("5x") ? "good" : "warn";
  if (key === "fiberAvailability")    return goodThreshold(FIBER_ORDER, value, 2, 4);
  if (key === "distanceToSubstation") return goodThreshold(SUBSTATION_ORDER, value, 2, 4);
  if (key === "landStatus")           return goodThreshold(LAND_ORDER, value, 2, 4);
  if (key === "timeline")             return goodThreshold(TIMELINE_ORDER, value, 2, 4);
  return "";
}

function shortVal(key, value) {
  if (key === "esgFramework") {
    const list = Array.isArray(value) ? value : (value ? [value] : []);
    if (list.length === 0) return "—";
    if (list.length === 1) return list[0].split(" /")[0].split(" (")[0];
    return `${list.length} frameworks`;
  }
  if (!value) return "—";
  if (key === "fiberAvailability") {
    if (value.includes("Carrier-neutral")) return "Carrier-neutral";
    if (value.includes("On-net")) return "On-net / IX";
    if (value.includes("Dual-path dark")) return "Dual dark fiber";
    if (value.includes("Dual-path")) return "Dual leased";
    if (value.includes("Single-path dark")) return "Single dark";
    if (value.includes("Single-path")) return "Single leased";
    return value;
  }
  if (key === "expandablePower") {
    if (value === "No expansion path") return "No expansion";
    if (value.includes("5x+")) return "5x+ long-term";
    return value.replace("Up to ", "").replace(" months", " mo");
  }
  if (key === "landStatus") return value.split(",")[0];
  if (key === "targetCustomers") {
    if (value.includes("hyperscalers")) return "Hyperscalers";
    if (value.includes("AI-native")) return "AI startups";
    if (value.includes("Fortune")) return "Enterprise";
    if (value.includes("Government")) return "Gov / Defense";
    if (value.includes("Telecom")) return "Telecom";
    return "Mixed";
  }
  return value;
}

// ─── Matrix Tab ───────────────────────────────────────────────────────────────
function MatrixTab({ repo, repoLoading, repoError, handleReEvaluate, handleReset, setTab, globalSelectedRisks, setGlobalSelectedRisks }) {
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [showRiskModal, setShowRiskModal] = useState(false);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const sorted = [...repo].sort((a, b) => {
    let av = sortKey === "score" ? (parseInt(a.score) || 0)
           : sortKey === "savedAt" ? (a.savedAt || "")
           : (a.fields?.[sortKey] ?? "");
    let bv = sortKey === "score" ? (parseInt(b.score) || 0)
           : sortKey === "savedAt" ? (b.savedAt || "")
           : (b.fields?.[sortKey] ?? "");
    if (typeof av === "number") return sortDir === "desc" ? bv - av : av - bv;
    return sortDir === "desc" ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
  });

  const tierA = repo.filter(s => String(s.tier).toUpperCase() === "A").length;
  const tierB = repo.filter(s => String(s.tier).toUpperCase() === "B").length;
  const tierC = repo.filter(s => String(s.tier).toUpperCase() === "C").length;
  const scores = repo.map(s => s.score != null ? parseInt(s.score) : null).filter(v => v != null && !isNaN(v));
  const avgScore = scores.length ? Math.round(scores.reduce((a,b) => a+b, 0) / scores.length) : 0;

  const exportCSV = () => {
    const headers = ["Project","Location","Score","Tier","Power","Expansion","Substation","Fiber","Land","Timeline","Customers","Capital Constraints","Risk Flags","Saved"];
    const rows = repo.map(s => {
      const riskIds = s.riskFlags || [];
      const riskNames = riskIds.map(id => ALL_RISKS.find(r => r.id === id)?.name || id).join("; ");
      return [
        s.fields?.projectName || "",
        s.fields?.location || "",
        s.score ?? "",
        s.tier ?? "",
        s.fields?.availablePower || "",
        s.fields?.expandablePower || "",
        s.fields?.distanceToSubstation || "",
        s.fields?.fiberAvailability || "",
        s.fields?.landStatus || "",
        s.fields?.timeline || "",
        s.fields?.targetCustomers || "",
        s.fields?.capitalConstraints || "",
        riskNames,
        s.savedAt ? new Date(s.savedAt).toLocaleDateString() : "",
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "t4-infra-pipeline.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-main">
      {showRiskModal && (
        <RiskFilterModal
          selectedRisks={globalSelectedRisks}
          onApply={(ids) => { setGlobalSelectedRisks(ids); setShowRiskModal(false); }}
          onClose={() => setShowRiskModal(false)}
        />
      )}

      <div className="mx-toolbar">
        <div className="mx-toolbar-left">
          <div className="section-tag">Comparison Matrix</div>
          <div className="mx-stats">
            <div className="mx-stat"><div className="mx-stat-val" style={{color:"#00ff88"}}>{tierA}</div><div className="mx-stat-label">TIER A</div></div>
            <div className="mx-stat"><div className="mx-stat-val" style={{color:"#f0a500"}}>{tierB}</div><div className="mx-stat-label">TIER B</div></div>
            <div className="mx-stat"><div className="mx-stat-val" style={{color:"#ff4444"}}>{tierC}</div><div className="mx-stat-label">TIER C</div></div>
            <div className="mx-stat"><div className="mx-stat-val" style={{color:"#e8e8e0"}}>{avgScore || "—"}</div><div className="mx-stat-label">AVG SCORE</div></div>
          </div>
          <button className={`risk-filter-btn ${globalSelectedRisks.length > 0 ? "active" : ""}`} onClick={() => setShowRiskModal(true)}>
            ⚑ Risk Filter
            {globalSelectedRisks.length > 0 && <span className="risk-count-badge">{globalSelectedRisks.length}</span>}
          </button>
        </div>
        <button className="mx-export-btn" onClick={exportCSV}>Export CSV</button>
      </div>

      <div className="mx-sort-row">
        <span className="mx-sort-label">Sort by</span>
        {[
          { key: "savedAt",        label: "Analysis Date" },
          { key: "score",          label: "Score"         },
          { key: "availablePower", label: "Power"         },
          { key: "timeline",       label: "Timeline"      },
        ].map(({ key, label }) => (
          <button key={key} className={`mx-sort-btn ${sortKey === key ? "active" : ""}`} onClick={() => handleSort(key)}>
            {label}{sortKey === key ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
          </button>
        ))}
      </div>

      {repoLoading ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"var(--muted)", fontSize:19, letterSpacing:"0.15em" }}>LOADING FROM SHEETS…</div>
      ) : repoError ? (
        <div className="error-state"><span>⚠</span> Could not connect to Google Sheets: {repoError}</div>
      ) : repo.length === 0 ? (
        <div className="mx-empty">
          <div className="mx-empty-title">No sites in pipeline</div>
          <div className="mx-empty-sub">Save evaluated sites to see them here.</div>
        </div>
      ) : (
        <div className="mx-table-wrap">
          <table className="mx-table">
            <colgroup>
              <col style={{width:"300px"}}/>
              <col style={{width:"120px"}}/>
              <col style={{width:"120px"}}/>
              <col style={{width:"130px"}}/>
              <col style={{width:"120px"}}/>
              <col style={{width:"150px"}}/>
              <col style={{width:"140px"}}/>
              <col style={{width:"120px"}}/>
              <col style={{width:"130px"}}/>
              <col style={{width:"100px"}}/>
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => handleSort("projectName")}>Site {sortKey==="projectName" ? (sortDir==="desc"?"↓":"↑") : ""}</th>
                <th className={sortKey==="score"?"sorted":""} onClick={() => handleSort("score")}>Score {sortKey==="score" ? (sortDir==="desc"?"↓":"↑") : ""}</th>
                {MATRIX_COLS.slice(1).map(col => (
                  <th key={col.key} className={sortKey===col.key?"sorted":""} onClick={() => col.sortable && handleSort(col.key)}>
                    {col.label}
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(site => {
                const sc = site.score != null ? parseInt(site.score) : null;
                const scoreColor = (sc != null && !isNaN(sc) && sc >= 80) ? "#00ff88" : (sc != null && !isNaN(sc) && sc >= 65) ? "#f0a500" : (sc != null && !isNaN(sc)) ? "#ff4444" : "#6a85a0";
                const tierColors = { A: "#00ff88", B: "#f0a500", C: "#ff4444" };
                const tc = tierColors[String(site.tier).toUpperCase()] || "#666";
                const siteRisks = (site.riskFlags || []).map(id => ALL_RISKS.find(r => r.id === id)).filter(Boolean);
                return (
                  <tr key={site.id}>
                    <td>
                      <div className="mx-site-name">{site.fields?.projectName || "Unnamed"}</div>
                      {site.fields?.location && <div className="mx-site-loc">{site.fields.location}</div>}
                      {siteRisks.length > 0 && (
                        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 2 }}>
                          {siteRisks.map(r => (
                            <span key={r.id} className={`risk-flag ${r.severity}`}>{r.name}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="mx-score-wrap">
                        <div className="mx-score-bar-track">
                          <div className="mx-score-bar-fill" style={{width:`${(sc != null && !isNaN(sc) ? sc : 0)}%`, background:scoreColor}}/>
                        </div>
                        <span className="mx-score-num" style={{color:scoreColor}}>{(sc != null && !isNaN(sc)) ? sc : "—"}</span>
                      </div>
                      {site.tier && (
                        <span style={{fontSize:"14px",fontWeight:500,letterSpacing:"0.12em",
                          padding:"1px 6px",borderRadius:"2px",marginTop:4,display:"inline-block",
                          background:`${tc}18`,color:tc,border:`1px solid ${tc}`}}>
                          TIER {String(site.tier).toUpperCase()}
                        </span>
                      )}
                    </td>
                    {MATRIX_COLS.slice(1).map(col => {
                      const val = site.fields?.[col.key];
                      const cls = pillClass(col.key, val);
                      return (
                        <td key={col.key}>
                          <span className={`mx-pill ${cls}`}>{shortVal(col.key, val)}</span>
                        </td>
                      );
                    })}
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-sm" onClick={(e) => { e.stopPropagation(); handleReEvaluate(site); }} style={{fontSize:"14px",padding:"4px 8px"}}>
                        ↺ Re-eval
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mx-footer">
        <div className="mx-footer-note">
          <div className="sheets-dot"/>
          Live from Google Sheets · {repo.length} site{repo.length !== 1 ? "s" : ""}
          {globalSelectedRisks.length > 0 && <span style={{color:"var(--amber)",marginLeft:4}}>· {globalSelectedRisks.length} risks flagged</span>}
        </div>
        <div className="mx-legend">
          <span style={{color:"#00cc66"}}>Green</span> = strong &nbsp;·&nbsp;
          <span style={{color:"#c88800"}}>Amber</span> = acceptable &nbsp;·&nbsp;
          <span style={{color:"#cc3333"}}>Red</span> = weak
        </div>
      </div>
    </div>
  );
}

// ─── Evaluate Tab ─────────────────────────────────────────────────────────────
function EvaluateTab({
  fields, handleChange, filledCount, progress,
  ingestFiles, ingesting, ingestError, ingestFields, ingestProgress, fileInputRef, handleFileSelect, handleDrop,
  editingSiteId, handleReset,
  loading, handleSubmit,
  result, saveStatus, handleSave,
  analysisProgress,
  error, currentMemo, resultsRef,
  selectedRisks, setSelectedRisks,
  handleSaveWithoutAnalysis, quickSaveStatus,
}) {
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const sevColor = { critical: "#ff6666", high: "#c88800", moderate: "#888890" };

  return (
    <div className="main">
      {showRiskModal && (
        <RiskFilterModal
          selectedRisks={selectedRisks}
          onApply={(ids) => { setSelectedRisks(ids); setShowRiskModal(false); }}
          onClose={() => setShowRiskModal(false)}
        />
      )}

      <div className="hero">
        <div className="section-tag">Pre-CIM Deal Intelligence</div>
        <h1>Structure any<br /><span>AI infrastructure</span><br />opportunity.</h1>
        <p>Upload a site document to auto-populate fields, or enter parameters manually. Flag risk factors to include in the analysis.</p>
      </div>

      {editingSiteId && (
        <div className="edit-banner">
          <span className="edit-banner-text">
            ✎ Re-evaluating: <strong>{fields.projectName || "existing site"}</strong> — run analysis then save to update the shared repository.
          </span>
          <button className="btn-sm" onClick={handleReset}>Cancel</button>
        </div>
      )}

      {/* Ingest zone with drag & drop */}
      <div
        className={`ingest-zone ${dragOver ? "drag-over" : ""}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleDrop(e.dataTransfer.files); }}
      >
        <div className="ingest-icon">{dragOver ? "↓" : "⬆"}</div>
        <div className="ingest-info">
          <div className="ingest-title">{dragOver ? "Drop to ingest" : "Ingest Site Document"}</div>
          <div className="ingest-sub">PDF, image, CSV, or TXT — drop multiple files to merge data from all</div>
          {ingesting && ingestProgress.total > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: "10px", color: "var(--green)", marginBottom: 5, display: "flex", justifyContent: "space-between" }}>
                <span>⟳ {ingestFiles[ingestProgress.current - 1]?.name || "Extracting…"}</span>
                <span style={{ color: "var(--amber)" }}>{ingestProgress.current}/{ingestProgress.total}</span>
              </div>
              <div className="prog-bar-outer" style={{ marginTop: 0 }}>
                <div className="prog-bar-inner" style={{ width: `${Math.round((ingestProgress.current / ingestProgress.total) * 100)}%` }} />
              </div>
            </div>
          )}
          {!ingesting && ingestFiles.length > 0 && !ingestError && ingestFields > 0 && (
            <div className="ingest-status">
              ✓ {ingestFields} field{ingestFields !== 1 ? "s" : ""} populated from {ingestFiles.length} file{ingestFiles.length !== 1 ? "s" : ""}
              {ingestFiles.length > 1 && `: ${ingestFiles.map(f => f.name).join(", ")}`}
            </div>
          )}
          {ingestError && <div className="ingest-err">⚠ {ingestError}</div>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button className="btn-upload" onClick={() => fileInputRef.current?.click()} disabled={ingesting}>
            {ingesting ? "Extracting…" : ingestFiles.length > 0 ? "Add / Replace Files" : "Upload Files"}
          </button>
          <button className={`risk-filter-btn ${selectedRisks.length > 0 ? "active" : ""}`} onClick={() => setShowRiskModal(true)}>
            ⚑ Risk Filter
            {selectedRisks.length > 0 && <span className="risk-count-badge">{selectedRisks.length}</span>}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt"
          style={{ display: "none" }} multiple onChange={handleFileSelect} />
      </div>

      {/* Selected risk flags preview */}
      {selectedRisks.length > 0 && (
        <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", padding: "10px 16px", marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: "9px", letterSpacing: "0.15em", color: "var(--muted)", marginRight: 4 }}>FLAGGED RISKS</span>
          {selectedRisks.map(id => {
            const risk = ALL_RISKS.find(r => r.id === id);
            if (!risk) return null;
            return <span key={id} className={`risk-flag ${risk.severity}`}>{risk.name}</span>;
          })}
        </div>
      )}

      <div className="progress-bar-wrap">
        <span className="progress-label">Fields Complete</span>
        <div className="progress-track"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
        <span className="progress-pct">{filledCount}/{FIELDS.length}</span>
      </div>

      <div className="form-grid">
        {FIELDS.map(field => {
          const wasExtracted = ingestFields > 0 && fields[field.key];
          return (
            <div key={field.key} className={`field-wrap ${(field.type === "textarea" || field.type === "esgpicker") ? "full" : ""}`}>
              <label className="field-label">
                <span className="dot" />
                {field.label}
                {wasExtracted && <span className="extracted">✓ extracted</span>}
              </label>
              {field.type === "esgpicker" ? (
                <ESGFrameworkPicker
                  value={fields.esgFramework || []}
                  otherValue={fields.esgOther || ""}
                  onChange={val => handleChange("esgFramework", val)}
                  onOtherChange={val => handleChange("esgOther", val)} />
              ) : field.type === "select" ? (
                <CustomSelect options={field.options} value={fields[field.key] || ""}
                  onChange={val => handleChange(field.key, val)} />
              ) : field.type === "textarea" ? (
                <textarea className={`field-textarea${wasExtracted ? " extracted-val" : ""}`}
                  placeholder={field.placeholder} value={fields[field.key] || ""}
                  onChange={e => handleChange(field.key, e.target.value)} />
              ) : field.validate ? (
                <div>
                  <input
                    className={`field-input${wasExtracted ? " extracted-val" : ""}${
                      fields[field.key] && validateCoord(fields[field.key], field.validate) ? " coord-error" :
                      fields[field.key] && !validateCoord(fields[field.key], field.validate) ? " coord-valid" : ""
                    }`}
                    type="text" placeholder={field.placeholder} value={fields[field.key] || ""}
                    onChange={e => handleChange(field.key, e.target.value)} />
                  {fields[field.key] && validateCoord(fields[field.key], field.validate) && (
                    <div className="coord-error-msg">{validateCoord(fields[field.key], field.validate)}</div>
                  )}
                </div>
              ) : (
                <input className={`field-input${wasExtracted ? " extracted-val" : ""}`}
                  type="text" placeholder={field.placeholder} value={fields[field.key] || ""}
                  onChange={e => handleChange(field.key, e.target.value)} />
              )}
            </div>
          );
        })}
      </div>

      <div className="actions">
        <button className="btn-secondary" onClick={handleSaveWithoutAnalysis} disabled={loading || filledCount < 1 || quickSaveStatus === "saving"}>
          {quickSaveStatus === "saving" ? <><div className="spinner-amber" />Saving…</>
            : quickSaveStatus === "saved" ? "✓ Saved"
            : quickSaveStatus === "error" ? "⚠ Error"
            : "Save without Analysis"}
        </button>
        <button className="btn-primary" onClick={handleSubmit} disabled={loading || filledCount < 3}>
          {loading ? <><div className="spinner" />Analyzing</> : "Run Analysis"}
        </button>
        {result && (
          <button className="btn-secondary" onClick={handleSave} disabled={saveStatus === "saving" || saveStatus === "saved"}>
            {saveStatus === "saving" ? <><div className="spinner-amber" />{editingSiteId ? "Updating…" : "Saving…"}</>
              : saveStatus === "saved" ? `✓ ${editingSiteId ? "Updated" : "Saved"} to Sheets`
              : editingSiteId ? "Update in Repository" : "Save to Repository"}
          </button>
        )}
        {saveStatus === "error" && <span className="save-status-error">⚠ Save failed — check console</span>}
        {(result || Object.keys(fields).length > 0) && (
          <button className="btn-ghost" onClick={handleReset}>Reset</button>
        )}
        {filledCount < 3 && !loading && (
          <span style={{ fontSize: "11px", color: "var(--muted)", letterSpacing: "0.1em" }}>
            Fill at least 3 fields to run
          </span>
        )}
      </div>

      {loading && (
        <div className="loading-state">
          <div style={{ fontSize: "11px", letterSpacing: "0.2em", color: "var(--muted)", textTransform: "uppercase" }}>
            Running infrastructure analysis{selectedRisks.length > 0 ? ` · ${selectedRisks.length} risk factors included` : ""}
          </div>
          <div className="analysis-prog-wrap">
            <div className="analysis-prog-label">
              <span style={{ color: "var(--muted)" }}>
                {analysisProgress < 20 ? "Parsing site parameters…"
                  : analysisProgress < 40 ? "Scoring power & fiber…"
                  : analysisProgress < 60 ? "Building financial model…"
                  : analysisProgress < 80 ? "Drafting investment memo…"
                  : analysisProgress < 95 ? "Finalizing deal strategy…"
                  : "Complete"}
              </span>
              <span>{Math.min(analysisProgress, 99)}%</span>
            </div>
            <div className="prog-bar-outer">
              <div className={`prog-bar-inner${analysisProgress >= 100 ? " green" : ""}`}
                style={{ width: `${analysisProgress}%` }} />
            </div>
          </div>
          <div className="loading-grid" style={{ marginTop: 16 }}>
            {["Power & Grid","Fiber Paths","Land & Permits","Latency Model","Cap Structure","Risk Scoring"].map((item, i) => (
              <div key={item} className="loading-item" style={{
                borderColor: analysisProgress > i * 16 ? "var(--amber-dim)" : "var(--border)",
                color: analysisProgress > i * 16 ? "var(--amber)" : "var(--muted)",
              }}>{item}</div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="error-state"><span>⚠</span>{error}</div>}

      {result && (
        <div className="results" ref={resultsRef}>
          <div className="results-header">
            <div>
              <div className="results-title">Analysis Output</div>
              {currentMemo && <TierBadge score={currentMemo.score} tier={currentMemo.tier} />}
            </div>
            <div className="timestamp">{new Date().toLocaleString()}</div>
          </div>
          {selectedRisks.length > 0 && (
            <div style={{ background: "rgba(240,165,0,0.05)", border: "1px solid rgba(240,165,0,0.2)", padding: "10px 16px", marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: "9px", letterSpacing: "0.15em", color: "var(--amber)", marginRight: 4, flexShrink: 0 }}>⚑ RISKS INCLUDED IN ANALYSIS</span>
              {selectedRisks.map(id => {
                const risk = ALL_RISKS.find(r => r.id === id);
                if (!risk) return null;
                return <span key={id} className={`risk-flag ${risk.severity}`}>{risk.name}</span>;
              })}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: "10px", letterSpacing: "0.15em", color: "var(--muted)", textTransform: "uppercase" }}>Full Investment Memo</div>
            <button className="btn-sm" onClick={() => generateMemoPDF(result, fields, currentMemo?.score, currentMemo?.tier)}
              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "10px" }}>
              ⬇ Create PDF
            </button>
          </div>
          <div className="results-content" dangerouslySetInnerHTML={{ __html: result }} />
        </div>
      )}
    </div>
  );
}

// ─── Re-Eval Modal ────────────────────────────────────────────────────────────
function ReEvalModal({ site, onSave, onClose }) {
  const [ready, setReady] = useState(false);
  const [localRisks, setLocalRisks] = useState(site.riskFlags || []);
  const [showRiskPicker, setShowRiskPicker] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [memo, setMemo] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const timerRef = useRef(null);
  const resultsRef = useRef(null);

  // Compute field diff against snapshot
  const changedFields = site.snapshotFields
    ? FIELDS.filter(f => JSON.stringify(site.fields?.[f.key] ?? null) !== JSON.stringify(site.snapshotFields?.[f.key] ?? null))
    : [];
  const fmtVal = v => Array.isArray(v) ? (v.join(", ") || "—") : (v || "—");

  useEffect(() => {
    if (!ready) return;
    setLoading(true);
    const steps = [8, 18, 30, 44, 58, 70, 80, 88, 92];
    let idx = 0;
    timerRef.current = setInterval(() => {
      if (idx < steps.length) { setProgress(steps[idx]); idx++; }
    }, 2800);

    fetch("/api/anthropic", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 4000,
        system: ANALYSIS_SYSTEM,
        messages: [{ role: "user", content: buildAnalysisPrompt(site.fields || {}, localRisks) }],
      }),
    })
      .then(r => r.json())
      .then(data => {
        const rawHtml = data.content?.map(b => b.text || "").join("") || "";
        const html = sanitizeAnalysisHtml(rawHtml);
        const { score, tier } = extractScoreAndTier(html);
        clearInterval(timerRef.current);
        setProgress(100);
        setResult(html);
        setMemo({ score, tier });
        setLoading(false);
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth" }), 400);
      })
      .catch(() => {
        clearInterval(timerRef.current);
        setError("Analysis failed. Please try again.");
        setLoading(false);
      });

    return () => clearInterval(timerRef.current);
  }, [ready]);

  const handleSave = async () => {
    if (!result || !memo) return;
    setSaving(true);
    try {
      const updated = { ...site, memo: result, score: memo.score, tier: memo.tier, memoStale: false, snapshotFields: { ...site.fields }, riskFlags: localRisks };
      await sheetsPut(updated);
      onSave(updated);
      setSaved(true);
      setTimeout(onClose, 1200);
    } catch (e) {
      setError("Save failed: " + e.message);
    } finally { setSaving(false); }
  };

  const phaseLabel = progress < 20 ? "Parsing site parameters…"
    : progress < 40 ? "Assessing power & grid readiness…"
    : progress < 55 ? "Evaluating fiber & connectivity…"
    : progress < 68 ? "Modeling latency & market fit…"
    : progress < 80 ? "Building financial model…"
    : progress < 90 ? "Drafting investment memo…"
    : progress < 99 ? "Finalizing deal strategy…"
    : "Complete";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: "20px" }}>
      {showRiskPicker && (
        <RiskFilterModal
          selectedRisks={localRisks}
          onApply={(ids) => { setLocalRisks(ids); setShowRiskPicker(false); }}
          onClose={() => setShowRiskPicker(false)}
        />
      )}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", width: "100%", maxWidth: "860px", maxHeight: "90vh", display: "flex", flexDirection: "column", fontFamily: "'DM Mono','Courier New',monospace" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 500, color: "var(--text)", letterSpacing: "0.05em" }}>Re-Evaluation</div>
            <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: 3, letterSpacing: "0.05em" }}>{site.fields?.projectName || "Unnamed Site"}{site.fields?.location ? ` · ${site.fields.location}` : ""}</div>
          </div>
          {!loading && <button onClick={onClose} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--muted)", fontFamily: "inherit", fontSize: "11px", padding: "5px 10px", cursor: "pointer" }}>✕ Close</button>}
        </div>

        {/* Pre-run screen */}
        {!ready && (
          <div style={{ overflowY: "auto", flex: 1, padding: "24px" }}>

            {/* Field diff */}
            {changedFields.length > 0 && (
              <div style={{ background: "rgba(0,200,255,0.04)", border: "1px solid rgba(0,200,255,0.2)", padding: "14px 18px", marginBottom: 16 }}>
                <div style={{ fontSize: 9, letterSpacing: "0.15em", color: "var(--amber)", marginBottom: 10 }}>FIELDS CHANGED SINCE LAST MEMO</div>
                {changedFields.map(f => (
                  <div key={f.key} style={{ fontSize: 11, marginBottom: 6, display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ color: "var(--muted)", minWidth: 140, flexShrink: 0 }}>{f.label}:</span>
                    <span style={{ color: "#ff6666", textDecoration: "line-through" }}>{fmtVal(site.snapshotFields?.[f.key])}</span>
                    <span style={{ color: "var(--muted)" }}>→</span>
                    <span style={{ color: "var(--green)" }}>{fmtVal(site.fields?.[f.key])}</span>
                  </div>
                ))}
              </div>
            )}
            {changedFields.length === 0 && site.snapshotFields && (
              <div style={{ fontSize: 11, color: "var(--muted)", padding: "10px 14px", border: "1px solid var(--border)", background: "var(--bg3)", marginBottom: 16 }}>
                No field changes since last memo was generated.
              </div>
            )}

            {/* Risk flags */}
            <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", padding: "14px 18px", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: localRisks.length > 0 ? 10 : 0 }}>
                <div style={{ fontSize: 9, letterSpacing: "0.15em", color: "var(--muted)", textTransform: "uppercase" }}>Risk Factors for this Re-Eval</div>
                <button className={`risk-filter-btn ${localRisks.length > 0 ? "active" : ""}`} onClick={() => setShowRiskPicker(true)} style={{ fontSize: "10px", padding: "4px 12px" }}>
                  ⚑ {localRisks.length > 0 ? "Edit Risks" : "Add Risks"}
                  {localRisks.length > 0 && <span className="risk-count-badge">{localRisks.length}</span>}
                </button>
              </div>
              {localRisks.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {localRisks.map(id => { const r = ALL_RISKS.find(x => x.id === id); return r ? <span key={id} className={`risk-flag ${r.severity}`}>{r.name}</span> : null; })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress (shown once running) */}
        {ready && (
          <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: "var(--muted)", letterSpacing: "0.1em", marginBottom: 8 }}>
              <span>{phaseLabel}</span>
              <span style={{ color: "var(--amber)" }}>{Math.min(progress, 99)}%</span>
            </div>
            <div style={{ height: 3, background: "var(--bg3)", overflow: "hidden" }}>
              <div style={{ height: "100%", background: progress >= 100 ? "var(--green)" : "var(--amber)", width: `${progress}%`, transition: "width 0.4s ease" }} />
            </div>
            {loading && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 14 }}>
                {["Power & Grid","Fiber Paths","Land & Permits","Latency Model","Cap Structure","Risk Scoring"].map((item, i) => (
                  <div key={item} className="loading-item" style={{ borderColor: progress > i * 16 ? "var(--amber-dim)" : "var(--border)", color: progress > i * 16 ? "var(--amber)" : "var(--muted)" }}>{item}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {ready && (
          <div style={{ overflowY: "auto", flex: 1, padding: "24px" }}>
            {error && <div className="error-state"><span>⚠</span>{error}</div>}
            {result && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: "10px", letterSpacing: "0.15em", color: "var(--muted)", textTransform: "uppercase" }}>Updated Analysis</span>
                    {memo && <TierBadge score={memo.score} tier={memo.tier} />}
                  </div>
                  <button className="btn-sm" onClick={() => generateMemoPDF(result, site.fields, memo?.score, memo?.tier)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "10px" }}>⬇ PDF</button>
                </div>
                <div ref={resultsRef} className="results-content" dangerouslySetInnerHTML={{ __html: result }} />
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          {!ready ? (
            <>
              <button onClick={onClose} className="btn-ghost" style={{ fontSize: "11px", padding: "10px 18px" }}>Cancel</button>
              <button onClick={() => setReady(true)} className="btn-primary" style={{ fontSize: "11px", padding: "10px 22px" }}>Run Re-Evaluation →</button>
            </>
          ) : result ? (
            <>
              <button onClick={onClose} className="btn-ghost" style={{ fontSize: "11px", padding: "10px 18px" }}>Discard</button>
              <button onClick={handleSave} disabled={saving || saved} className="btn-primary" style={{ fontSize: "11px", padding: "10px 22px" }}>
                {saved ? "✓ Saved" : saving ? <><span className="spinner" />Saving…</> : "Save to Repository"}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Edit Site Modal ──────────────────────────────────────────────────────────
function EditSiteModal({ site, onSave, onClose }) {
  const [localFields, setLocalFields] = useState({ ...site.fields });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const updated = { ...site, fields: { ...localFields }, memoStale: true };
      await sheetsPut(updated);
      onSave(updated);
    } catch (e) {
      setError("Save failed: " + e.message);
    } finally { setSaving(false); }
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 500, padding: "20px",
    }}>
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", width: "100%", maxWidth: "760px",
        maxHeight: "88vh", display: "flex", flexDirection: "column", fontFamily: "'DM Mono','Courier New',monospace" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 500, color: "var(--text)", letterSpacing: "0.05em" }}>Edit Site</div>
            <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: 3, letterSpacing: "0.05em" }}>{site.fields?.projectName || "Unnamed Site"}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--muted)", fontFamily: "inherit", fontSize: "11px", padding: "5px 10px", cursor: "pointer" }}>✕ Close</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "24px", flex: 1 }}>
          <div className="form-grid">
            {FIELDS.map(field => (
              <div key={field.key} className={`field-wrap ${(field.type === "textarea" || field.type === "esgpicker") ? "full" : ""}`}>
                <label className="field-label"><span className="dot" />{field.label}</label>
                {field.type === "esgpicker" ? (
                  <ESGFrameworkPicker
                    value={localFields.esgFramework || []}
                    otherValue={localFields.esgOther || ""}
                    onChange={val => setLocalFields(p => ({ ...p, esgFramework: val }))}
                    onOtherChange={val => setLocalFields(p => ({ ...p, esgOther: val }))} />
                ) : field.type === "select" ? (
                  <CustomSelect options={field.options} value={localFields[field.key] || ""}
                    onChange={val => setLocalFields(p => ({ ...p, [field.key]: val }))} />
                ) : field.type === "textarea" ? (
                  <textarea className="field-textarea" placeholder={field.placeholder}
                    value={localFields[field.key] || ""}
                    onChange={e => setLocalFields(p => ({ ...p, [field.key]: e.target.value }))} />
                ) : (
                  <input className="field-input" type="text" placeholder={field.placeholder || ""}
                    value={localFields[field.key] || ""}
                    onChange={e => setLocalFields(p => ({ ...p, [field.key]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
          {error && <div className="error-state"><span>⚠</span>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ fontSize: "11px", padding: "10px 18px" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ fontSize: "11px", padding: "10px 22px" }}>
            {saving ? <><span className="spinner" />Saving…</> : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Square Tier Badge ────────────────────────────────────────────────────────
function SqTier({ score, tier, size = "sm" }) {
  const t = tier ? String(tier).toUpperCase() : null;
  const dim = size === "lg" ? 90 : 74;
  const scoreFs = size === "lg" ? 38 : 32;
  const c = t === "A" ? "#00ff88" : t === "B" ? "#f0a500" : t === "C" ? "#ff4444" : "#6a85a0";
  const bg = t === "A" ? "rgba(0,255,136,0.06)" : t === "B" ? "rgba(240,165,0,0.06)" : t === "C" ? "rgba(255,68,68,0.06)" : "transparent";
  const border = t ? c : "#2e4060";
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      width:dim, height:dim, border:`1px solid ${border}`, background:bg, flexShrink:0 }}>
      <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontWeight:800, lineHeight:1, fontSize:scoreFs, color:c }}>
        {score != null ? score : "—"}
      </span>
      <span style={{ fontSize:13, letterSpacing:"0.14em", opacity:0.8, marginTop:1, color:c }}>
        {t ? `TIER ${t}` : "N/A"}
      </span>
    </div>
  );
}

// ─── Repository Tab ───────────────────────────────────────────────────────────
function RepositoryTab({
  repo, repoLoading, repoError,
  viewingSite, setViewingSite,
  deletingId, handleDelete, handleReEvaluate,
  handleSaveRiskFlags, handleEditSave,
  handleReset, setTab,
}) {
  const [editingRisks, setEditingRisks] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tierFilter, setTierFilter] = useState(null);
  const [staleFilter, setStaleFilter] = useState(false);
  const [riskFilter, setRiskFilter] = useState(false);
  return (
    <div className="main">
      {editingSite && (
        <EditSiteModal
          site={editingSite}
          onSave={(updated) => { handleEditSave(updated); setEditingSite(null); }}
          onClose={() => setEditingSite(null)} />
      )}
      <div className="repo-header">
        <div>
          <div className="section-tag">Site Pipeline</div>
          <div className="repo-title">Location Repository</div>
          <div className="sheets-badge" style={{ marginTop: 8 }}>
            <div className="sheets-dot" />
            Synced to Google Sheets
          </div>
        </div>
        <button className="btn-primary" onClick={() => { handleReset(); setTab("evaluate"); }} style={{ fontSize: "18px", alignSelf: "flex-start" }}>
          + New Site
        </button>
      </div>

      {repoLoading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)", fontSize: 19, letterSpacing: "0.15em" }}>
          LOADING FROM SHEETS…
        </div>
      ) : repoError ? (
        <div className="error-state"><span>⚠</span> Could not connect to Google Sheets: {repoError}</div>
      ) : repo.length === 0 ? (
        <div className="repo-empty">
          <div className="repo-empty-title">No sites saved yet</div>
          <div className="repo-empty-sub">Run an analysis and click "Save to Repository" to build your shared pipeline.</div>
        </div>
      ) : (
        <>
          {/* Search bar */}
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name or location…" className="field-input"
            style={{ width: "100%", fontSize: 19, padding: "8px 12px", marginBottom: 10 }} />

          {/* Filter badges */}
          {(() => {
            const cntA = repo.filter(s => String(s.tier).toUpperCase() === "A").length;
            const cntB = repo.filter(s => String(s.tier).toUpperCase() === "B").length;
            const cntC = repo.filter(s => String(s.tier).toUpperCase() === "C").length;
            const cntStale = repo.filter(s => s.memoStale).length;
            const cntRisks = repo.filter(s => Array.isArray(s.riskFlags) ? s.riskFlags.length > 0 : false).length;
            const badgeBase = { display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", fontSize:16, fontFamily:"'DM Mono','Courier New',monospace", letterSpacing:"0.1em", textTransform:"uppercase", border:"1px solid #2e4060", background:"transparent", color:"#7a95b0", cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s" };
            const activeTierStyle = (active, col) => active ? { ...badgeBase, borderColor:col, color:col, background:`${col}18` } : badgeBase;
            return (
              <div style={{ display:"flex", flexDirection:"row", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:16 }}>
                <span style={{ fontSize:14, letterSpacing:"0.18em", color:"#4a6080", flexShrink:0 }}>FILTER</span>
                <button style={activeTierStyle(tierFilter==="A","#00ff88")} onClick={() => setTierFilter(f => f==="A" ? null : "A")}>Tier A <span style={{ opacity:0.6, fontSize:14 }}>({cntA})</span></button>
                <button style={activeTierStyle(tierFilter==="B","#00c8ff")} onClick={() => setTierFilter(f => f==="B" ? null : "B")}>Tier B <span style={{ opacity:0.6, fontSize:14 }}>({cntB})</span></button>
                <button style={activeTierStyle(tierFilter==="C","#ff4444")} onClick={() => setTierFilter(f => f==="C" ? null : "C")}>Tier C <span style={{ opacity:0.6, fontSize:14 }}>({cntC})</span></button>
                <div style={{ width:1, height:14, background:"#2e4060", flexShrink:0, margin:"0 2px" }} />
                <button style={{ ...badgeBase, opacity: cntStale===0?0.3:1, ...(staleFilter ? { borderColor:"#f0a500", color:"#f0a500", background:"rgba(240,165,0,0.08)" } : {}) }} onClick={() => setStaleFilter(f => !f)}>⚠ Stale <span style={{ opacity:0.6, fontSize:14 }}>({cntStale})</span></button>
                <button style={{ ...badgeBase, opacity: cntRisks===0?0.3:1, ...(riskFilter ? { borderColor:"#ff8c42", color:"#ff8c42", background:"rgba(255,140,66,0.08)" } : {}) }} onClick={() => setRiskFilter(f => !f)}>⚑ Has Risks <span style={{ opacity:0.6, fontSize:14 }}>({cntRisks})</span></button>
                {(tierFilter || staleFilter || riskFilter) && (
                  <>
                    <div style={{ width:1, height:14, background:"#2e4060", flexShrink:0, margin:"0 2px" }} />
                    <button style={{ ...badgeBase, color:"#6a85a0" }} onClick={() => { setTierFilter(null); setStaleFilter(false); setRiskFilter(false); }}>✕ Clear</button>
                  </>
                )}
              </div>
            );
          })()}

          <div className="site-list">
            {repo.filter(site => {
              const q = searchQuery.trim().toLowerCase();
              const matchSearch = !q || (site.fields?.projectName || "").toLowerCase().includes(q) || (site.fields?.location || "").toLowerCase().includes(q);
              const matchTier = !tierFilter || String(site.tier).toUpperCase() === tierFilter;
              const matchStale = !staleFilter || !!site.memoStale;
              const matchRisks = !riskFilter || (site.riskFlags || []).length > 0;
              return matchSearch && matchTier && matchStale && matchRisks;
            }).map(site => {
              const siteRisks = (site.riskFlags || []).map(id => ALL_RISKS.find(r => r.id === id)).filter(Boolean);
              return (
                <div key={site.id} className={`site-card ${viewingSite?.id === site.id ? "active" : ""}`}
                  onClick={() => setViewingSite(viewingSite?.id === site.id ? null : site)}>
                  <SqTier score={site.score != null ? parseInt(site.score) : null} tier={site.tier} size="sm" />
                  <div className="site-card-info">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div className="site-card-name">{site.fields?.projectName || "Unnamed Site"}</div>
                      {site.memoStale && <span style={{ fontSize: 14, color: "#f0a500", background: "rgba(240,165,0,0.08)", border: "1px solid rgba(240,165,0,0.25)", padding: "1px 6px", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>⚠ MEMO STALE</span>}
                    </div>
                    <div className="site-card-meta">
                      {site.fields?.location && <span>📍 {site.fields.location}</span>}
                      {site.fields?.availablePower && <span>⚡ {site.fields.availablePower}</span>}
                      {site.fields?.timeline && <span>⏱ {site.fields.timeline}</span>}
                      {site.savedAt && <span>{new Date(site.savedAt).toLocaleDateString()}</span>}
                      {site.fileName && <span>📄 {site.fileName}</span>}
                    </div>
                    {siteRisks.length > 0 && (
                      <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {siteRisks.map(r => <span key={r.id} className={`risk-flag ${r.severity}`}>{r.name}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="site-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="btn-sm" onClick={() => setEditingSite(site)}>✎ Edit</button>
                    <button className="btn-sm" onClick={() => handleReEvaluate(site)}>↺ Re-eval</button>
                    {confirmDeleteId === site.id ? (
                      <>
                        <button className="btn-sm danger" disabled={deletingId === site.id}
                          onClick={() => { setConfirmDeleteId(null); handleDelete(site.id); }}>
                          {deletingId === site.id ? "…" : "Confirm"}
                        </button>
                        <button className="btn-sm" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-sm danger" disabled={deletingId === site.id}
                        onClick={() => setConfirmDeleteId(site.id)}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {viewingSite && (
            <div className="site-detail">
              <hr className="divider" />
              {editingRisks && (
                <RiskFilterModal
                  selectedRisks={viewingSite.riskFlags || []}
                  onApply={(ids) => { handleSaveRiskFlags(viewingSite.id, ids); setEditingRisks(false); }}
                  onClose={() => setEditingRisks(false)}
                />
              )}
              <div className="site-detail-header">
                {/* Left — title, meta, stale warning */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="site-detail-name">{viewingSite.fields?.projectName || "Unnamed Site"}</div>
                  <div className="site-detail-meta">
                    Saved {viewingSite.savedAt ? new Date(viewingSite.savedAt).toLocaleString() : "—"}
                    {viewingSite.fileName && ` · Source: ${viewingSite.fileName}`}
                  </div>
                  {viewingSite.memoStale && (
                    <div style={{ fontSize: 16, color: "#f0a500", marginTop: 6, letterSpacing: "0.05em" }}>
                      ⚠ Memo is stale — re-evaluate to update.
                    </div>
                  )}
                </div>
                {/* Right — score badge + action buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                  <SqTier score={viewingSite.score != null ? parseInt(viewingSite.score) : null} tier={viewingSite.tier} size="lg" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <button className="btn-sm" onClick={() => handleReEvaluate(viewingSite)}>↺ Re-eval</button>
                    <button className="btn-sm"
                      onClick={() => generateMemoPDF(viewingSite.memo, viewingSite.fields, viewingSite.score != null ? parseInt(viewingSite.score) : null, viewingSite.tier)}
                      style={{ display: "flex", alignItems: "center", gap: 4 }}>⬇ PDF</button>
                    <button className={`btn-sm ${(viewingSite.riskFlags || []).length > 0 ? "" : ""}`}
                      onClick={() => setEditingRisks(true)}
                      style={(viewingSite.riskFlags || []).length > 0 ? { borderColor: "var(--amber)", color: "var(--amber)" } : {}}>
                      ⚑ Risks{(viewingSite.riskFlags || []).length > 0 && ` (${viewingSite.riskFlags.length})`}
                    </button>
                  </div>
                </div>
              </div>
              <div className="site-fields-grid">
                {FIELDS.filter(f => f.type !== "textarea" && f.type !== "esgpicker" && viewingSite.fields?.[f.key]).map(f => (
                  <div key={f.key} className="site-field">
                    <div className="site-field-label">{f.label}</div>
                    <div className="site-field-value">{viewingSite.fields[f.key]}</div>
                  </div>
                ))}
              </div>
              {(() => {
                const fw = viewingSite.fields?.esgFramework;
                const other = viewingSite.fields?.esgOther;
                const list = Array.isArray(fw) ? fw : (fw ? [fw] : []);
                const all = other ? [...list, `Other: ${other}`] : list;
                if (all.length === 0) return null;
                return (
                  <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", padding: "14px 18px", marginBottom: 16 }}>
                    <div style={{ fontSize: 16, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>ESG Reporting Frameworks</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {all.map(f => <span key={f} style={{ fontSize: 18, background: "rgba(0,200,255,0.06)", border: "1px solid rgba(0,200,255,0.2)", color: "var(--text)", padding: "2px 8px" }}>{f}</span>)}
                    </div>
                  </div>
                );
              })()}
              {(viewingSite.riskFlags || []).length > 0 && (
                <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", padding: "14px 18px", marginBottom: 16 }}>
                  <div style={{ fontSize: 16, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Flagged Risks</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {viewingSite.riskFlags.map(id => {
                      const r = ALL_RISKS.find(x => x.id === id);
                      return r ? <span key={id} className={`risk-flag ${r.severity}`}>{r.name}</span> : null;
                    })}
                  </div>
                </div>
              )}
              {viewingSite.fields?.notes && (
                <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", padding: "14px 18px", marginBottom: 16 }}>
                  <div style={{ fontSize: 16, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>Notes</div>
                  <div style={{ fontSize: 21, color: "#c8c8be", lineHeight: 1.6 }}>{viewingSite.fields.notes}</div>
                </div>
              )}
              {viewingSite.fields?.capitalConstraints && (
                <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", padding: "14px 18px", marginBottom: 16 }}>
                  <div style={{ fontSize: 16, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>Capital Constraints</div>
                  <div style={{ fontSize: 21, color: "#c8c8be" }}>{viewingSite.fields.capitalConstraints}</div>
                </div>
              )}
              <div style={{ fontSize: 18, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 14 }}>Full Investment Memo</div>
              <div className="results-content" dangerouslySetInnerHTML={{ __html: viewingSite.memo }} />
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─── PDF Generation ───────────────────────────────────────────────────────────
function generateMemoPDF(memoHtml, siteFields, score, tier) {
  const VERSION = APP_VERSION;
  const projectName = siteFields?.projectName || "Site Evaluation";
  const location = siteFields?.location || "";
  const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const fieldSummary = [
    { label: "Available Power", key: "availablePower" },
    { label: "Expandable Power", key: "expandablePower" },
    { label: "Distance to Substation", key: "distanceToSubstation" },
    { label: "Fiber Availability", key: "fiberAvailability" },
    { label: "Latency Requirements", key: "latencyRequirements" },
    { label: "Target Customers", key: "targetCustomers" },
    { label: "Land Status", key: "landStatus" },
    { label: "Timeline to First Power", key: "timeline" },
    { label: "Latitude", key: "latitude" },
    { label: "Longitude", key: "longitude" },
    { label: "Capital Constraints", key: "capitalConstraints" },
  ].filter(f => {
    const v = siteFields?.[f.key];
    return Array.isArray(v) ? v.length > 0 : !!v;
  });

  const tierColors = { A: "#1a7a4a", B: "#b07800", C: "#b03030" };
  const tierBg = { A: "#e8f7ef", B: "#fef8e7", C: "#fdeaea" };
  const tc = tierColors[tier] || "#444";
  const tb = tierBg[tier] || "#f5f5f5";

  // Convert dark-theme HTML to light-theme by stripping inline dark styles
  const lightMemo = memoHtml
    .replace(/color:\s*#c8c8be/gi, "color:#2a2a2a")
    .replace(/color:\s*#e8e8e0/gi, "color:#111111")
    .replace(/color:\s*#888890/gi, "color:#555555")
    .replace(/background:\s*rgba\(26,26,36[^)]*\)/gi, "background:#f8f8f8")
    .replace(/background:\s*var\(--bg[23]\)/gi, "background:#f8f8f8")
    .replace(/border:\s*1px solid #2a2a38/gi, "border:1px solid #dddddd")
    .replace(/border:\s*1px solid var\(--border\)/gi, "border:1px solid #dddddd");

  const pdfHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>${projectName} — T4 Infra Eval ${VERSION}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Syne:wght@700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Mono','Courier New',monospace;background:#ffffff;color:#111111;padding:48px;max-width:900px;margin:0 auto;font-size:12px;line-height:1.7}
  .pdf-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #111111}
  .pdf-logo{display:flex;align-items:center;gap:10px}
  .pdf-hex{width:20px;height:20px;background:#00c8ff;clip-path:polygon(25% 0%,75% 0%,100% 50%,75% 100%,25% 100%,0% 50%)}
  .pdf-brand{font-family:'Bebas Neue',sans-serif;font-size:13px;font-weight:800;letter-spacing:0.15em;color:#111111}
  .pdf-brand-sub{font-size:9px;color:#666666;letter-spacing:0.18em;margin-top:2px}
  .pdf-meta{text-align:right;font-size:10px;color:#666666}
  .pdf-title{font-family:'Bebas Neue',sans-serif;font-size:28px;font-weight:800;color:#111111;margin-bottom:6px}
  .pdf-location{font-size:13px;color:#666666;margin-bottom:20px;letter-spacing:0.05em}
  .pdf-score-row{display:flex;align-items:center;gap:16px;margin-bottom:28px}
  .pdf-score{font-family:'Bebas Neue',sans-serif;font-size:42px;font-weight:800;color:#111111;line-height:1}
  .pdf-tier{font-family:'Bebas Neue',sans-serif;font-size:13px;font-weight:700;letter-spacing:0.18em;padding:4px 14px;background:${tb};color:${tc};border:1.5px solid ${tc}}
  .pdf-fields{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:32px}
  .pdf-field{background:#f5f5f5;border:1px solid #e0e0e0;padding:8px 12px}
  .pdf-field-label{font-size:8px;letter-spacing:0.18em;color:#888888;text-transform:uppercase;margin-bottom:3px}
  .pdf-field-value{font-size:11px;color:#111111;font-weight:500}
  .pdf-divider{border:none;border-top:1.5px solid #111111;margin:28px 0}
  .pdf-memo-label{font-size:9px;letter-spacing:0.2em;color:#888888;text-transform:uppercase;margin-bottom:16px}
  .memo h2{font-family:'Bebas Neue',sans-serif;font-size:14px;font-weight:700;color:#c88000;letter-spacing:0.05em;text-transform:uppercase;margin:28px 0 10px;padding-bottom:6px;border-bottom:1px solid #dddddd}
  .memo h2:first-child{margin-top:0}
  .memo h3{font-family:'Bebas Neue',sans-serif;font-size:11px;font-weight:600;color:#111111;letter-spacing:0.08em;text-transform:uppercase;margin:16px 0 6px}
  .memo p{font-size:11px;color:#2a2a2a;margin-bottom:8px;line-height:1.7}
  .memo ul{margin:6px 0 12px 0;padding:0;list-style:none}
  .memo li{font-size:11px;color:#2a2a2a;padding:4px 0 4px 14px;position:relative;border-bottom:1px solid #eeeeee}
  .memo li::before{content:'›';position:absolute;left:0;color:#c88000}
  .memo strong{color:#111111;font-weight:600}
  .memo table{width:100%;border-collapse:collapse;font-size:10px;margin:12px 0}
  .memo th{background:#f0f0f0;color:#c88000;text-align:left;padding:7px 10px;font-size:9px;letter-spacing:0.12em;text-transform:uppercase;border:1px solid #dddddd}
  .memo td{padding:6px 10px;color:#2a2a2a;border:1px solid #dddddd;vertical-align:top}
  .memo tr:nth-child(even) td{background:#fafafa}
  .memo .score-badge{display:inline-block;background:#f5f5f5;border:2px solid #c88000;color:#c88000;font-family:'Bebas Neue',sans-serif;font-size:28px;font-weight:800;padding:8px 18px;margin:6px 0}
  .memo .tier-badge{display:inline-block;font-family:'Bebas Neue',sans-serif;font-size:10px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;padding:3px 10px;margin-left:8px;vertical-align:middle}
  .memo .tier-a{background:#e8f7ef;color:#1a7a4a;border:1px solid #1a7a4a}
  .memo .tier-b{background:#fef8e7;color:#b07800;border:1px solid #b07800}
  .memo .tier-c{background:#fdeaea;color:#b03030;border:1px solid #b03030}
  .pdf-footer{margin-top:40px;padding-top:14px;border-top:1px solid #dddddd;display:flex;justify-content:space-between;font-size:9px;color:#aaaaaa;letter-spacing:0.08em}
  @media print{body{padding:24px}.pdf-header{page-break-inside:avoid}}
</style>
</head>
<body>
  <div class="pdf-header">
    <div class="pdf-logo">
      <div class="pdf-hex"></div>
      <div>
        <div class="pdf-brand">TIER 4 SOLUTIONS</div>
        <div class="pdf-brand-sub">AI INFRASTRUCTURE DEAL INTELLIGENCE</div>
      </div>
    </div>
    <div class="pdf-meta">
      <div>${VERSION}</div>
      <div style="margin-top:3px">${now}</div>
      <div style="margin-top:3px;font-weight:500;color:#111111">INVESTMENT MEMO</div>
    </div>
  </div>

  <div class="pdf-title">${projectName}</div>
  ${location ? `<div class="pdf-location">📍 ${location}${siteFields?.latitude && siteFields?.longitude ? ` &nbsp;·&nbsp; ${siteFields.latitude}, ${siteFields.longitude}` : ""}</div>` : ""}

  ${score != null || tier ? `
  <div class="pdf-score-row">
    ${score != null ? `<div class="pdf-score">${score}</div>` : ""}
    ${tier ? `<div class="pdf-tier">TIER ${tier}</div>` : ""}
  </div>` : ""}

  ${fieldSummary.length > 0 ? `
  <div class="pdf-fields">
    ${fieldSummary.map(f => `
    <div class="pdf-field">
      <div class="pdf-field-label">${f.label}</div>
      <div class="pdf-field-value">${Array.isArray(siteFields[f.key]) ? siteFields[f.key].join(", ") : siteFields[f.key]}</div>
    </div>`).join("")}
  </div>` : ""}

  ${siteFields?.esgNotes ? `
  <div style="background:#f5f5f5;border:1px solid #e0e0e0;padding:10px 14px;margin-bottom:24px">
    <div style="font-size:8px;letter-spacing:0.18em;color:#888888;text-transform:uppercase;margin-bottom:4px">ESG Profile</div>
    <div style="font-size:11px;color:#2a2a2a;line-height:1.6">${siteFields.esgNotes}</div>
  </div>` : ""}
  <hr class="pdf-divider"/>
  <div class="pdf-memo-label">Full Investment Memo</div>
  <div class="memo">${lightMemo}</div>

  <div class="pdf-footer">
    <div>T4 DevCo · AI Infrastructure Deal Intelligence · ${VERSION}</div>
    <div>CONFIDENTIAL — FOR INTERNAL USE ONLY</div>
  </div>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) { alert("Pop-up blocked — please allow pop-ups for this site."); return; }
  win.document.write(pdfHtml);
  win.document.close();
  setTimeout(() => win.print(), 800);
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState("evaluate");
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const analysisTimerRef = useRef(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [currentMemo, setCurrentMemo] = useState(null);
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [reEvalSite, setReEvalSite] = useState(null);
  const [selectedRisks, setSelectedRisks] = useState([]);
  const [globalSelectedRisks, setGlobalSelectedRisks] = useState([]);

  const [repo, setRepo] = useState([]);
  const [repoLoading, setRepoLoading] = useState(true);
  const [repoError, setRepoError] = useState(null);
  const [viewingSite, setViewingSite] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [ingestFiles, setIngestFiles] = useState([]);
  const [ingesting, setIngesting] = useState(false);
  const [ingestError, setIngestError] = useState(null);
  const [ingestFields, setIngestFields] = useState(0);
  const [ingestProgress, setIngestProgress] = useState({ current: 0, total: 0 });

  const resultsRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    sheetsGet()
      .then(sites => { setRepo(sites); setRepoLoading(false); })
      .catch(e => { setRepoError(e.message); setRepoLoading(false); });
  }, []);

  const handleChange = useCallback((key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  }, []);

  const extractFromFile = async (file) => {
    const isTextFile = file.type === "text/csv" || file.type === "text/plain" || file.name.endsWith(".csv") || file.name.endsWith(".txt");
    let messageContent;
    if (isTextFile) {
      const text = await file.text();
      messageContent = [{ type: "text", text: `Extract infrastructure site data from this document:\n\n${text.slice(0, 8000)}` }];
    } else if (file.type === "application/pdf") {
      const b64 = await fileToBase64(file);
      messageContent = [
        { type: "document", source: { type: "base64", media_type: "application/pdf", data: b64 } },
        { type: "text", text: "Extract infrastructure site data from this document." }
      ];
    } else if (file.type.startsWith("image/")) {
      const b64 = await fileToBase64(file);
      messageContent = [
        { type: "image", source: { type: "base64", media_type: file.type, data: b64 } },
        { type: "text", text: "Extract infrastructure site data from this document." }
      ];
    } else {
      throw new Error(`Unsupported file type: ${file.name}`);
    }
    const response = await fetch("/api/anthropic", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, system: EXTRACT_SYSTEM, messages: [{ role: "user", content: messageContent }] }),
    });
    const data = await response.json();
    const raw = data.content?.map(b => b.text || "").join("") || "";
    return JSON.parse(raw.replace(/```json|```/g, "").trim());
  };

  const processFiles = async (fileList) => {
    const files = Array.from(fileList).filter(f =>
      f.type === "text/csv" || f.type === "text/plain" || f.type === "application/pdf" ||
      f.type.startsWith("image/") || f.name.endsWith(".csv") || f.name.endsWith(".txt")
    );
    if (files.length === 0) return;
    setIngestFiles(files);
    setIngestError(null);
    setIngesting(true);
    setIngestFields(0);
    setIngestProgress({ current: 0, total: files.length });

    const merged = {};
    let totalFields = 0;
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      setIngestProgress({ current: i + 1, total: files.length });
      try {
        const parsed = await extractFromFile(files[i]);
        FIELDS.forEach(f => {
          // Only fill gaps — don't overwrite already-populated fields from earlier files
          if (parsed[f.key] != null && parsed[f.key] !== "" && !merged[f.key]) {
            merged[f.key] = parsed[f.key];
            totalFields++;
          }
        });
      } catch (err) {
        errors.push(files[i].name);
      }
    }

    if (Object.keys(merged).length > 0) {
      setFields(prev => ({ ...prev, ...merged }));
      setIngestFields(totalFields);
    }
    if (errors.length > 0) {
      setIngestError(`Could not extract from: ${errors.join(", ")}`);
    }
    setIngesting(false);
  };

  const handleFileSelect = (e) => { if (e.target.files?.length) processFiles(e.target.files); };
  const handleDrop = (fileList) => { if (fileList?.length) processFiles(fileList); };

  const handleSubmit = async () => {
    setLoading(true); setResult(null); setError(null); setSaveStatus(null); setCurrentMemo(null);
    setAnalysisProgress(0);
    // Simulate progress — steps advance every ~3s, capping at 90% until response arrives
    const steps = [8, 18, 30, 44, 58, 70, 80, 88, 92];
    let stepIdx = 0;
    analysisTimerRef.current = setInterval(() => {
      if (stepIdx < steps.length) { setAnalysisProgress(steps[stepIdx]); stepIdx++; }
    }, 2800);
    try {
      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 4000,
          system: ANALYSIS_SYSTEM,
          messages: [{ role: "user", content: buildAnalysisPrompt(fields, selectedRisks) }]
        }),
      });
      const data = await response.json();
      const rawHtml = data.content?.map(b => b.text || "").join("") || "";
      const html = sanitizeAnalysisHtml(rawHtml);
      const { score, tier } = extractScoreAndTier(html);
      clearInterval(analysisTimerRef.current);
      setAnalysisProgress(100);
      setResult(html);
      setCurrentMemo({ score, tier, html });
      setTimeout(() => { setAnalysisProgress(0); resultsRef.current?.scrollIntoView({ behavior: "smooth" }); }, 600);
    } catch {
      clearInterval(analysisTimerRef.current);
      setAnalysisProgress(0);
      setError("Analysis failed. Please try again.");
    }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!result) return;
    setSaveStatus("saving");
    try {
      const { score, tier } = currentMemo || {};
      if (editingSiteId) {
        const existing = repo.find(s => s.id === editingSiteId);
        const updated = { ...existing, fields: { ...fields }, score, tier, memo: result, fileName: ingestFiles.length > 0 ? ingestFiles.map(f => f.name).join(', ') : existing.fileName, riskFlags: selectedRisks, memoStale: false, snapshotFields: { ...fields } };
        await sheetsPut(updated);
        setRepo(prev => prev.map(s => s.id === editingSiteId ? updated : s));
        if (viewingSite?.id === editingSiteId) setViewingSite(updated);
      } else {
        const site = { id: Date.now().toString(), savedAt: new Date().toISOString(), fields: { ...fields }, score, tier, memo: result, fileName: ingestFiles.length > 0 ? ingestFiles.map(f => f.name).join(', ') : null, riskFlags: selectedRisks, memoStale: false, snapshotFields: { ...fields } };
        await sheetsPost(site);
        setRepo(prev => [site, ...prev]);
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch { setSaveStatus("error"); }
  };

  const [quickSaveStatus, setQuickSaveStatus] = useState(null);

  const handleSaveWithoutAnalysis = async () => {
    if (filledCount < 1) return;
    setQuickSaveStatus("saving");
    try {
      if (editingSiteId) {
        const existing = repo.find(s => s.id === editingSiteId);
        const updated = { ...existing, fields: { ...fields }, fileName: ingestFiles.length > 0 ? ingestFiles.map(f => f.name).join(', ') : existing.fileName, riskFlags: selectedRisks, memoStale: !!existing?.memo };
        await sheetsPut(updated);
        setRepo(prev => prev.map(s => s.id === editingSiteId ? updated : s));
        if (viewingSite?.id === editingSiteId) setViewingSite(updated);
      } else {
        const site = { id: Date.now().toString(), savedAt: new Date().toISOString(), fields: { ...fields }, score: null, tier: null, memo: null, fileName: ingestFiles.length > 0 ? ingestFiles.map(f => f.name).join(', ') : null, riskFlags: selectedRisks };
        await sheetsPost(site);
        setRepo(prev => [site, ...prev]);
      }
      setQuickSaveStatus("saved");
      setTimeout(() => setQuickSaveStatus(null), 3000);
    } catch { setQuickSaveStatus("error"); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await sheetsDelete(id);
      setRepo(prev => prev.filter(s => s.id !== id));
      if (viewingSite?.id === id) setViewingSite(null);
    } catch (e) { alert("Delete failed: " + e.message); }
    finally { setDeletingId(null); }
  };

  const handleSaveRiskFlags = async (siteId, riskIds) => {
    const site = repo.find(s => s.id === siteId);
    if (!site) return;
    const updated = { ...site, riskFlags: riskIds };
    try {
      await sheetsPut(updated);
      setRepo(prev => prev.map(s => s.id === siteId ? updated : s));
      setViewingSite(updated);
    } catch (e) { alert("Failed to save risk flags: " + e.message); }
  };

  const handleEditSave = (updated) => {
    const withStale = { ...updated, memoStale: true };
    setRepo(prev => prev.map(s => s.id === withStale.id ? withStale : s));
    if (viewingSite?.id === withStale.id) setViewingSite(withStale);
  };

  const handleReEvaluate = (site) => {
    setReEvalSite(site);
  };

  const handleReset = useCallback(() => {
    setFields({}); setResult(null); setError(null); setSaveStatus(null);
    setCurrentMemo(null); setEditingSiteId(null);
    setSelectedRisks([]);
    setIngestFiles([]); setIngestFields(0); setIngestError(null); setIngestProgress({ current: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const filledCount = FIELDS.filter(f => {
    const v = fields[f.key];
    if (Array.isArray(v)) return v.length > 0;
    return typeof v === "string" ? v.trim().length > 0 : !!v;
  }).length;
  const progress = (filledCount / FIELDS.length) * 100;

  return (
    <div style={{ fontFamily: "'DM Mono','Courier New',monospace", background: "#080c12", minHeight: "100vh", color: "#d4dfe8" }}>
      <style>{CSS}</style>
      <header className="header">
        <div className="logo">
          <div className="logo-icon" />
          <div>
            <div className="logo-text">Tier 4 Solutions</div>
            <div className="logo-sub">AI Infrastructure Deal Intelligence</div>
          </div>
        </div>
        <div className="status-bar">
          <span style={{ fontSize: "10px", letterSpacing: "0.12em", color: "#666678", background: "#1a1a24", border: "1px solid #2a2a38", padding: "2px 8px" }}>{APP_VERSION}</span>
          <span style={{ color: "#2a2a38", margin: "0 4px" }}>|</span>
          <div className="status-dot" />SYSTEM ONLINE
        </div>
      </header>

      <div className="tabs">
        <button className={`tab ${tab === "evaluate" ? "active" : ""}`} onClick={() => setTab("evaluate")}>
          Evaluate Site
        </button>
        <button className={`tab ${tab === "repository" ? "active" : ""}`} onClick={() => setTab("repository")}>
          Repository <span className="tab-count">{repo.length}</span>
        </button>
        <button className={`tab ${tab === "matrix" ? "active" : ""}`} onClick={() => setTab("matrix")}>
          Matrix <span className="tab-count">{repo.length}</span>
        </button>
      </div>

      {tab === "evaluate" && (
        <EvaluateTab
          fields={fields} handleChange={handleChange}
          filledCount={filledCount} progress={progress}
          ingestFiles={ingestFiles} ingesting={ingesting}
          ingestError={ingestError} ingestFields={ingestFields} ingestProgress={ingestProgress}
          fileInputRef={fileInputRef} handleFileSelect={handleFileSelect}
          handleDrop={handleDrop}
          editingSiteId={editingSiteId} handleReset={handleReset}
          loading={loading} handleSubmit={handleSubmit}
          result={result} saveStatus={saveStatus} handleSave={handleSave}
          analysisProgress={analysisProgress}
          error={error} currentMemo={currentMemo} resultsRef={resultsRef}
          selectedRisks={selectedRisks} setSelectedRisks={setSelectedRisks}
          handleSaveWithoutAnalysis={handleSaveWithoutAnalysis} quickSaveStatus={quickSaveStatus}
        />
      )}
      {reEvalSite && (
        <ReEvalModal
          site={reEvalSite}
          onSave={(updated) => { handleEditSave(updated); setReEvalSite(null); }}
          onClose={() => setReEvalSite(null)}
        />
      )}
      {tab === "repository" && (
        <RepositoryTab
          repo={repo} repoLoading={repoLoading} repoError={repoError}
          viewingSite={viewingSite} setViewingSite={setViewingSite}
          deletingId={deletingId} handleDelete={handleDelete}
          handleReEvaluate={handleReEvaluate}
          handleSaveRiskFlags={handleSaveRiskFlags}
          handleEditSave={handleEditSave}
          handleReset={handleReset} setTab={setTab}
        />
      )}
      {tab === "matrix" && (
        <MatrixTab
          repo={repo} repoLoading={repoLoading} repoError={repoError}
          handleReEvaluate={handleReEvaluate}
          handleReset={handleReset} setTab={setTab}
          globalSelectedRisks={globalSelectedRisks}
          setGlobalSelectedRisks={setGlobalSelectedRisks}
        />
      )}
    </div>
  );
}
